import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  RadialBarChart, RadialBar, Legend
} from 'recharts';
import {
  FiShield, FiAlertTriangle, FiCheck, FiClock, FiLayers,
  FiChevronDown, FiChevronRight, FiPlus, FiLoader, FiTrash2,
  FiInfo, FiX, FiActivity, FiDatabase, FiZap, FiSettings,
  FiToggleLeft, FiToggleRight, FiCpu, FiTag, FiLock,
  FiGlobe, FiKey, FiDollarSign, FiBarChart2, FiSearch, FiEdit2
} from 'react-icons/fi';
import { useTenant } from '../components/Layout';
import { getFrameworksForProvider, noMatch } from '../data/complianceFrameworks';
import NoTenantGuard from '../components/NoTenantGuard';
import {
  getDriftReport, setBaseline, getBaselines, activateBaseline,
  deleteBaseline, syncTenant, getRuleTemplates, getBaselineRules,
  addBaselineRule, deleteRule, toggleRule, updateRule,
  getCloudPolicies, getCompliancePosture,
  getControlResources, getAIPolicyRemediation
} from '../api';

const SEVERITY_COLORS = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#2563eb' };
const SEVERITY_BG = { critical: '#fef2f2', high: '#fff7ed', medium: '#fffbeb', low: '#eff6ff' };
const CHANGE_ICONS = { modified: '~', added: '+', removed: '-', tag_removed: '\uD83C\uDFF7' };

const CATEGORY_META = {
  'NSG Rule Modification': { icon: '\uD83D\uDEE1\uFE0F', group: 'Network Security', color: '#7c3aed' },
  'VM Configuration Change': { icon: '\uD83D\uDDA5\uFE0F', group: 'Compute', color: '#2563eb' },
  'Disk Encryption Disabled': { icon: '\uD83D\uDD12', group: 'Security', color: '#dc2626' },
  'Disk Encryption Changed': { icon: '\uD83D\uDD12', group: 'Security', color: '#dc2626' },
  'Tag Removal': { icon: '\uD83C\uDFF7\uFE0F', group: 'Governance', color: '#d97706' },
  'Tag Removed': { icon: '\uD83C\uDFF7\uFE0F', group: 'Governance', color: '#d97706' },
  'Storage Public Access Enabled': { icon: '\uD83D\uDCE6', group: 'Storage', color: '#ea580c' },
  'Public Access Enabled': { icon: '\uD83D\uDCE6', group: 'Storage', color: '#ea580c' },
  'SQL Firewall Rule Added': { icon: '\uD83D\uDDC4\uFE0F', group: 'Database', color: '#059669' },
  'Firewall Rule Added': { icon: '\uD83D\uDDC4\uFE0F', group: 'Database', color: '#059669' },
  'Key Vault Access Change': { icon: '\uD83D\uDD11', group: 'Security', color: '#dc2626' },
  'Access Policy Changed': { icon: '\uD83D\uDD11', group: 'Security', color: '#dc2626' },
};

const CATEGORY_COLORS = ['#7c3aed', '#2563eb', '#dc2626', '#d97706', '#ea580c', '#059669', '#0891b2', '#be185d'];

const TEMPLATE_ICONS = {
  cpu: FiCpu, tag: FiTag, lock: FiLock, shield: FiShield,
  globe: FiGlobe, database: FiDatabase, key: FiKey,
  'dollar': FiDollarSign, 'bar-chart': FiBarChart2,
  layers: FiLayers, activity: FiActivity, zap: FiZap,
  settings: FiSettings, 'alert-triangle': FiAlertTriangle, info: FiInfo
};

const OPERATOR_LABELS = {
  equals: 'Must Equal', not_equals: 'Not Equal To', max: 'Maximum Value',
  min: 'Minimum Value', in: 'Must Be In List', not_in: 'Must Not Be In',
  contains_keys: 'Must Contain Keys', not_contains: 'Must Not Contain', max_count: 'Max Count',
  boolean_required: 'Boolean Required', contains: 'Must Contain'
};

const RULE_CATEGORIES_BY_PROVIDER = {
  azure: {
    'Identity & Access': { icon: '\uD83D\uDD11', color: '#7c3aed', types: ['Microsoft.Authorization', 'Azure AD', 'RBAC'] },
    'Governance & Policy': { icon: '\uD83D\uDCDC', color: '#d97706', types: ['tags', 'location', 'resource_group', 'policy'] },
    'Network Security': { icon: '\uD83D\uDEE1\uFE0F', color: '#2563eb', types: ['Microsoft.Network'] },
    'Compute & Infrastructure': { icon: '\uD83D\uDDA5\uFE0F', color: '#059669', types: ['Microsoft.Compute', 'Microsoft.ContainerService', 'Microsoft.Web'] },
    'Security & Defender': { icon: '\uD83D\uDD12', color: '#dc2626', types: ['Microsoft.KeyVault', 'encryption', 'Defender', 'firewall'] },
    'Cost & Tagging': { icon: '\uD83D\uDCB0', color: '#ea580c', types: ['premium', 'sku', 'tier', 'cost', 'tag'] },
  },
  aws: {
    'Identity & Access': { icon: '\uD83D\uDD11', color: '#7c3aed', types: ['AWS::IAM', 'IAM', 'MFA'] },
    'Governance & Policy': { icon: '\uD83D\uDCDC', color: '#d97706', types: ['tags', 'location', 'resource_group', 'policy'] },
    'Network Security': { icon: '\uD83D\uDEE1\uFE0F', color: '#2563eb', types: ['AWS::EC2::SecurityGroup', 'AWS::EC2::VPC'] },
    'Compute & Infrastructure': { icon: '\uD83D\uDDA5\uFE0F', color: '#059669', types: ['AWS::EC2::Instance', 'AWS::Lambda', 'AWS::ECS', 'AWS::EKS'] },
    'Security & Defender': { icon: '\uD83D\uDD12', color: '#dc2626', types: ['encryption', 'SecurityHub', 'GuardDuty', 'CloudTrail'] },
    'Cost & Tagging': { icon: '\uD83D\uDCB0', color: '#ea580c', types: ['premium', 'instance_type', 'cost', 'tag', 'budget'] },
  },
  gcp: {
    'Identity & Access': { icon: '\uD83D\uDD11', color: '#7c3aed', types: ['iam.googleapis.com', 'IAM', 'ServiceAccount'] },
    'Governance & Policy': { icon: '\uD83D\uDCDC', color: '#d97706', types: ['tags', 'location', 'resource_group', 'policy', 'labels'] },
    'Network Security': { icon: '\uD83D\uDEE1\uFE0F', color: '#2563eb', types: ['compute.googleapis.com/Network', 'firewall'] },
    'Compute & Infrastructure': { icon: '\uD83D\uDDA5\uFE0F', color: '#059669', types: ['compute.googleapis.com/Instance', 'cloudfunctions.googleapis.com', 'container.googleapis.com'] },
    'Security & Defender': { icon: '\uD83D\uDD12', color: '#dc2626', types: ['encryption', 'SecurityCommandCenter', 'audit', 'kms'] },
    'Cost & Tagging': { icon: '\uD83D\uDCB0', color: '#ea580c', types: ['premium', 'machine_type', 'cost', 'label', 'budget'] },
  },
};
const RULE_CATEGORIES = RULE_CATEGORIES_BY_PROVIDER.azure;

const RESOURCE_TYPES_BY_PROVIDER = {
  azure: [
    { value: '*', label: 'All Resources' },
    { value: 'Microsoft.Compute/virtualMachines', label: 'Virtual Machines' },
    { value: 'Microsoft.Storage/storageAccounts', label: 'Storage Accounts' },
    { value: 'Microsoft.Network/networkSecurityGroups', label: 'Network Security Groups' },
    { value: 'Microsoft.Network/virtualNetworks', label: 'Virtual Networks' },
    { value: 'Microsoft.Sql/servers', label: 'SQL Servers' },
    { value: 'Microsoft.KeyVault/vaults', label: 'Key Vaults' },
    { value: 'Microsoft.Web/sites', label: 'App Services' },
    { value: 'Microsoft.ContainerService/managedClusters', label: 'AKS Clusters' },
  ],
  aws: [
    { value: '*', label: 'All Resources' },
    { value: 'AWS::EC2::Instance', label: 'EC2 Instances' },
    { value: 'AWS::S3::Bucket', label: 'S3 Buckets' },
    { value: 'AWS::EC2::SecurityGroup', label: 'Security Groups' },
    { value: 'AWS::EC2::VPC', label: 'VPCs' },
    { value: 'AWS::RDS::DBInstance', label: 'RDS Instances' },
    { value: 'AWS::Lambda::Function', label: 'Lambda Functions' },
    { value: 'AWS::IAM::User', label: 'IAM Users' },
    { value: 'AWS::EKS::Cluster', label: 'EKS Clusters' },
  ],
  gcp: [
    { value: '*', label: 'All Resources' },
    { value: 'compute.googleapis.com/Instance', label: 'Compute Engine VMs' },
    { value: 'storage.googleapis.com/Bucket', label: 'Cloud Storage Buckets' },
    { value: 'compute.googleapis.com/Network', label: 'VPC Networks' },
    { value: 'sqladmin.googleapis.com/Instance', label: 'Cloud SQL Instances' },
    { value: 'cloudfunctions.googleapis.com/Function', label: 'Cloud Functions' },
    { value: 'iam.googleapis.com/ServiceAccount', label: 'Service Accounts' },
    { value: 'container.googleapis.com/Cluster', label: 'GKE Clusters' },
  ],
};

function getChangeExplanation(change) {
  const prop = (change.property_changed || '').toLowerCase();
  const cat = (change.category || '').toLowerCase();
  const bv = change.baseline_value || '';
  const cv = change.current_value || '';
  const isRule = !!change.rule_id;

  if (isRule) {
    const ruleName = change.rule_name || 'governance rule';
    if (bv.startsWith('Expected:'))
      return `This resource violates the "${ruleName}" rule. The rule requires ${bv.replace('Expected: ', '')} but the resource currently has a value of "${cv}".`;
    if (bv.startsWith('Max '))
      return `Resource group exceeds the limit set by "${ruleName}". The maximum allowed is ${bv.replace('Max ', '')} resources, but ${cv} resources currently exist in this group.`;
    return `The "${ruleName}" rule expected "${bv}" but found "${cv}" on this resource.`;
  }

  if (prop === 'securityrules' || cat.includes('nsg') || cat.includes('security group')) {
    const baseNum = parseInt(bv) || 0;
    const currNum = parseInt(cv) || 0;
    const diff = currNum - baseNum;
    if (diff > 0)
      return `${diff} new security rule${diff > 1 ? 's were' : ' was'} added to this security group since the baseline was captured. It went from ${baseNum} rules to ${currNum} rules. These new rules may open additional ports or IP ranges that were not originally approved.`;
    if (diff < 0)
      return `${Math.abs(diff)} security rule${Math.abs(diff) > 1 ? 's were' : ' was'} removed from this security group. It went from ${baseNum} rules to ${currNum} rules. Removed rules may leave expected traffic blocked.`;
    return `The security rules on this resource were modified. Baseline had ${bv}, currently has ${cv}.`;
  }

  if (prop === 'vmsize' || cat.includes('vm configuration')) {
    return `This VM was resized from ${bv} to ${cv}. This changes the CPU, memory, and cost of the VM. The new size "${cv}" was not in the baseline and may not be approved for this workload.`;
  }

  if (prop === 'osdiskencryption' || cat.includes('encryption') || cat.includes('disk')) {
    if (cv.toLowerCase() === 'disabled' || cv.toLowerCase() === 'false')
      return `Disk encryption was turned OFF on this VM. The baseline required encryption to be "${bv}". Unencrypted disks expose data at rest if the underlying storage is accessed.`;
    return `Disk encryption setting changed from "${bv}" to "${cv}".`;
  }

  if (prop === 'tags' || cat.includes('tag')) {
    if (cv.toLowerCase().includes('removed'))
      return `Mandatory governance tags were removed from this resource. The following tags were present at baseline: ${bv}. Without these tags, the resource cannot be tracked for cost allocation, ownership, or lifecycle management.`;
    return `Tags on this resource changed from "${bv}" to "${cv}".`;
  }

  if (prop === 'allowblobpublicaccess' || prop === 'publicaccess' || cat.includes('public access') || cat.includes('storage')) {
    if (cv.toLowerCase() === 'true')
      return `Public access was ENABLED on this storage resource. At baseline it was set to "${bv}" (disabled). Any object set to "public" access level is now accessible to anyone on the internet without authentication.`;
    return `Public access setting changed from "${bv}" to "${cv}".`;
  }

  if (prop === 'firewallrules' || cat.includes('firewall') || cat.includes('sql') || cat.includes('database')) {
    if (cv.toLowerCase().includes('allowall') || cv.includes('0.0.0.0'))
      return `A permissive firewall rule was added that allows connections from ANY IP address (0.0.0.0 - 255.255.255.255). At baseline, ${bv.toLowerCase()}. This effectively removes all network-level access control from this database resource.`;
    return `Firewall configuration changed from "${bv}" to "${cv}".`;
  }

  if (prop === 'accesspolicies' || cat.includes('access policy') || cat.includes('key vault') || cat.includes('secrets manager')) {
    const bNum = parseInt(bv) || 0;
    const cNum = parseInt(cv) || 0;
    const diff = cNum - bNum;
    if (diff > 0)
      return `${diff} new access ${diff > 1 ? 'policies were' : 'policy was'} added to this secrets/key management resource. It went from ${bNum} to ${cNum} access policies. The new policy grants permissions to an identity not present in the baseline, which may be an unrecognized service principal.`;
    return `Access policies changed from "${bv}" to "${cv}".`;
  }

  return null;
}

function getImpactDescription(change) {
  const cat = (change.category || '').toLowerCase();
  const prop = (change.property_changed || '').toLowerCase();
  const desc = (change.description || '').toLowerCase();
  const isRule = !!change.rule_id;

  if (isRule) {
    if (desc.includes('location') || desc.includes('region'))
      return 'Non-compliant resource region may violate data residency requirements and regulatory mandates. Could result in audit findings.';
    if (desc.includes('tag'))
      return 'Missing required tags break cost allocation, ownership tracking, and automated governance policies.';
    if (desc.includes('encryption') || desc.includes('disk'))
      return 'Encryption non-compliance exposes data at rest. Regulatory frameworks (SOC 2, ISO 27001, HIPAA) require encryption on all managed disks.';
    if (desc.includes('public') || desc.includes('access'))
      return 'Public access violations create direct data exposure risk. Sensitive data may be accessible without authentication.';
    if (desc.includes('size') || desc.includes('vm'))
      return 'Unapproved VM sizes may exceed cost budgets or use instance types not validated for the workload security requirements.';
    if (desc.includes('firewall'))
      return 'Overly permissive firewall rules expose database services to unauthorized network access, increasing attack surface.';
    return 'This resource does not comply with the governance rule defined in your baseline. Review and remediate to maintain compliance posture.';
  }

  if (cat.includes('nsg') || cat.includes('security group') || prop.includes('securityrules'))
    return 'Unauthorized inbound rules weaken network perimeter security. Attackers can exploit newly opened ports for lateral movement or data exfiltration.';
  if (cat.includes('vm') || cat.includes('instance') || prop.includes('vmsize') || prop.includes('instancetype'))
    return 'Unapproved compute instance resizing impacts cost forecasting and may bypass change management. Larger instance types incur significantly higher hourly compute costs.';
  if (cat.includes('encryption') || cat.includes('disk'))
    return 'Disabling disk encryption violates compliance requirements (CIS, SOC 2, PCI-DSS). Data on unencrypted disks is readable if the physical storage is compromised.';
  if (cat.includes('tag'))
    return 'Removing mandatory tags breaks cost reporting, ownership tracking, and automated lifecycle policies. Resources become ungovernable.';
  if (cat.includes('public') || cat.includes('storage'))
    return 'Enabling public access exposes storage contents to the internet. Any object set to public will serve data without authentication.';
  if (cat.includes('firewall') || cat.includes('sql') || cat.includes('database'))
    return 'Adding a permissive firewall rule exposes the database to the entire internet. This is the most common entry point for database breaches.';
  if (cat.includes('access') || cat.includes('key vault') || cat.includes('secrets'))
    return 'Unauthorized access policy changes to secrets/key management may grant secret/key/certificate access to unrecognized identities, risking credential theft.';

  return null;
}

function getRecommendedAction(change) {
  const cat = (change.category || '').toLowerCase();
  const prop = (change.property_changed || '').toLowerCase();
  const desc = (change.description || '').toLowerCase();
  const isRule = !!change.rule_id;

  if (isRule) {
    if (desc.includes('location') || desc.includes('region'))
      return 'Move the resource to an approved region, or update the governance policy if the region has been approved. Use cloud policy controls to prevent future deployments to non-approved regions.';
    if (desc.includes('tag'))
      return 'Re-apply the required tags to this resource. Set up cloud policy enforcement to auto-apply missing mandatory tags on new deployments.';
    if (desc.includes('encryption'))
      return 'Re-enable encryption on the affected resource. Use cloud policy controls with deny effect to prevent creation of unencrypted resources.';
    if (desc.includes('public') || desc.includes('access'))
      return 'Disable public access on this resource immediately. Use cloud policy controls to prevent future public access enablement.';
    if (desc.includes('size') || desc.includes('vm'))
      return 'Resize the instance to an approved SKU/type. Use cloud policy controls to restrict instance sizes to your approved list in production environments.';
    if (desc.includes('firewall'))
      return 'Remove the overly permissive firewall rule. Restrict access to specific IP ranges or use private endpoints. Enable advanced threat protection.';
    return 'Remediate this resource to comply with the baseline rule. Review the expected vs. actual values above and correct the configuration.';
  }

  if (cat.includes('nsg') || prop.includes('securityrules') || cat.includes('security group'))
    return 'Review and remove unauthorized security rules. Verify which rules were added, by whom, and whether they are needed. Enable flow logs and restrict access.';
  if (cat.includes('vm') || cat.includes('instance') || prop.includes('vmsize') || prop.includes('instancetype'))
    return 'Verify the resize was authorized via change management. If not, resize back to the approved SKU/type. Enable resource locks on production instances to prevent unauthorized changes.';
  if (cat.includes('encryption') || cat.includes('disk'))
    return 'Re-enable encryption immediately. Investigate who disabled it and when via cloud activity logs. Set up policy controls to deny unencrypted resources.';
  if (cat.includes('tag'))
    return 'Re-apply the removed tags. Set up cloud policy enforcement to automatically re-tag resources. Review activity logs to identify who removed the tags.';
  if (cat.includes('public') || cat.includes('storage'))
    return 'Disable public access on this storage resource immediately. Audit all containers/buckets for public access level. Use cloud policy to deny public access.';
  if (cat.includes('firewall') || cat.includes('sql'))
    return 'Remove the overly permissive firewall rule. Configure specific IP-based rules or use private endpoints. Enable auditing and advanced threat protection.';
  if (cat.includes('access') || cat.includes('key vault') || cat.includes('secrets'))
    return 'Review and remove the unauthorized access policy. Audit the service principal/role identity. Consider migrating to RBAC-based access for secrets management.';

  return 'Review this change in your cloud activity logs. Verify if it was authorized. Revert to baseline configuration if the change was not approved through change management.';
}

function categorizeRule(rule, provider = 'azure') {
  const combined = `${rule.target_resource_type || ''} ${rule.property_path || ''} ${rule.name || ''}`.toLowerCase();
  const cats = RULE_CATEGORIES_BY_PROVIDER[provider] || RULE_CATEGORIES_BY_PROVIDER.azure;
  for (const [cat, info] of Object.entries(cats)) {
    if (info.types.some(t => combined.includes(t.toLowerCase()))) return cat;
  }
  return 'Governance & Policy';
}

function isDuplicateRule(existing, newRule) {
  return existing.some(r =>
    r.name.toLowerCase() === newRule.name.toLowerCase() ||
    (r.target_resource_type === newRule.target_resource_type &&
     r.property_path === newRule.property_path &&
     r.operator === newRule.operator &&
     r.expected_value === newRule.expected_value)
  );
}

function TemplateConfigModal({ template, existingRules, onClose, onAdd }) {
  const Icon = TEMPLATE_ICONS[template.icon] || FiSettings;
  const [severity, setSeverity] = useState(template.severity || 'high');
  const [expectedValue, setExpectedValue] = useState(template.default_value || '');
  const [scope, setScope] = useState('*');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const isDuplicate = useMemo(() => {
    return existingRules.some(r =>
      r.name.toLowerCase() === template.name.toLowerCase() ||
      (r.target_resource_type === template.target_resource_type &&
       r.property_path === template.property_path &&
       r.operator === template.operator)
    );
  }, [existingRules, template]);

  const handleAdd = async () => {
    if (isDuplicate) {
      setError('Rule already exists in this baseline.');
      return;
    }
    setAdding(true);
    setError(null);
    try {
      await onAdd({
        name: template.name,
        rule_type: template.rule_type || 'property_match',
        target_resource_type: template.target_resource_type,
        target_scope: scope,
        property_path: template.property_path,
        operator: template.operator,
        expected_value: expectedValue,
        severity: severity,
      });
      onClose();
    } catch (e) {
      setError('Failed to add rule. Please try again.');
    }
    setAdding(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 520,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: '1.25rem 1.5rem', background: 'linear-gradient(135deg, #eff6ff, #f0f9ff)',
          borderBottom: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: '0.75rem'
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10, background: '#2563eb',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon size={20} style={{ color: 'white' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e40af' }}>Configure: {template.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#3b82f6' }}>{template.category}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>{template.description}</p>

          {isDuplicate && (
            <div style={{
              padding: '0.6rem 0.85rem', borderRadius: 8, background: '#fffbeb',
              border: '1px solid #fde68a', fontSize: '0.8rem', color: '#92400e',
              marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 6
            }}>
              <FiAlertTriangle size={14} /> Rule already exists in this baseline.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Scope</label>
              <select value={scope} onChange={e => setScope(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                <option value="*">All Resources</option>
                <option value="subscription">Subscription Level</option>
                <option value="rg-prod">Production Resource Groups</option>
                <option value="rg-dev">Development Resource Groups</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>
              Parameter Value ({OPERATOR_LABELS[template.operator] || template.operator})
            </label>
            <input value={expectedValue} onChange={e => setExpectedValue(e.target.value)}
              style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
            <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 3 }}>
              Target: {template.target_resource_type === '*' ? 'All resources' : template.target_resource_type} &rarr; {template.property_path}
            </div>
          </div>

          {error && (
            <div style={{
              padding: '0.5rem 0.75rem', borderRadius: 6, background: '#fef2f2',
              border: '1px solid #fecaca', fontSize: '0.78rem', color: '#991b1b', marginBottom: '0.75rem'
            }}>{error}</div>
          )}

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer'
            }}>Cancel</button>
            <button onClick={handleAdd} disabled={adding || isDuplicate || !expectedValue.trim()} style={{
              padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: 'none', background: isDuplicate ? '#d1d5db' : '#2563eb', color: 'white',
              cursor: isDuplicate ? 'not-allowed' : 'pointer', opacity: adding ? 0.6 : 1
            }}>
              {adding ? 'Adding...' : 'Add Rule'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const PROPERTY_OPTIONS_BY_PROVIDER = {
  azure: {
    '*': [
      { path: 'location', label: 'Location / Region', description: 'Azure region where the resource is deployed', suggestedName: 'Approved Regions Only', valueOptions: ['eastus', 'westus2', 'westeurope', 'northeurope', 'southeastasia', 'japaneast', 'australiaeast', 'uksouth', 'canadacentral'] },
      { path: 'tags', label: 'Required Tag Keys', description: 'Enforce mandatory tag keys on all resources', suggestedName: 'Required Tags', valueOptions: ['Environment,Owner,CostCenter', 'Environment,Owner', 'CostCenter,BudgetCode,Department', 'Owner,Team,Project', 'Environment,Owner,CostCenter,Project,ExpiryDate'] },
      { path: 'name', label: 'Resource Name / Naming Convention', description: 'Enforce a naming prefix or convention', suggestedName: 'Resource Naming Convention', valueOptions: ['prod-,dev-,stg-', 'eus-,wus-,weu-', 'platform-,app-,data-', 'corp-,shared-'] },
      { path: 'resource_group', label: 'Resource Group Count', description: 'Limit resources per resource group', suggestedName: 'Max Resources per Resource Group', valueOptions: ['25', '50', '75', '100'] },
    ],
    'Microsoft.Compute/virtualMachines': [
      { path: 'properties.hardwareProfile.vmSize', label: 'VM Size / SKU', description: 'Restrict which VM sizes are allowed', suggestedName: 'Approved VM Sizes', valueOptions: ['Standard_D2s_v3', 'Standard_D4s_v3', 'Standard_D8s_v3', 'Standard_B2ms', 'Standard_B4ms', 'Standard_E4s_v3', 'Standard_F4s_v2'] },
      { path: 'properties.osDiskEncryption', label: 'OS Disk Encryption', description: 'Enforce encryption on VM OS disks', suggestedName: 'Require Disk Encryption', valueOptions: ['Enabled'] },
      { path: 'properties.storageProfile.osDisk.managedDisk', label: 'Managed Disk Required', description: 'Require use of Azure Managed Disks', suggestedName: 'Managed Disks Only', valueOptions: ['true'] },
      { path: 'properties.diagnosticsProfile.bootDiagnostics.enabled', label: 'Boot Diagnostics Enabled', description: 'Require boot diagnostics for troubleshooting VM startup', suggestedName: 'Boot Diagnostics Required', valueOptions: ['true', 'false'] },
      { path: 'properties.storageProfile.imageReference.publisher', label: 'OS Image Publisher', description: 'Restrict to approved OS image publishers', suggestedName: 'Approved OS Images Only', valueOptions: ['Canonical', 'MicrosoftWindowsServer', 'RedHat', 'SUSE', 'credativ', 'Oracle'] },
      { path: 'properties.publicIpAddress', label: 'Public IP Address', description: 'Prevent VMs from having public IPs directly assigned', suggestedName: 'No Direct Public IP on VMs', valueOptions: ['none', 'null'] },
      { path: 'properties.autoShutdownSchedule', label: 'Auto-Shutdown Schedule', description: 'Enforce auto-shutdown to reduce idle compute costs', suggestedName: 'Auto-Shutdown Required', valueOptions: ['19:00', '20:00', '18:00', '22:00'] },
    ],
    'Microsoft.Network/networkSecurityGroups': [
      { path: 'properties.securityRules', label: 'Max NSG Rules Count', description: 'Maximum number of security rules per NSG', suggestedName: 'NSG Rule Limit', valueOptions: ['5', '10', '15', '20', '30'] },
      { path: 'properties.securityRules.sourceAddressPrefix', label: 'Block Allow-All Source', description: 'Prevent rules that allow traffic from any source (0.0.0.0/0)', suggestedName: 'No Allow-All Inbound Rules', valueOptions: ['*', '0.0.0.0/0'] },
      { path: 'properties.securityRules.dangerousPorts', label: 'Blocked Ports from Internet', description: 'Block SSH / RDP / Telnet access from internet', suggestedName: 'Block SSH and RDP from Internet', valueOptions: ['22,3389', '22,23,3389', '22,23,3389,5985,5986', '3389'] },
    ],
    'Microsoft.Network/virtualNetworks': [
      { path: 'properties.dhcpOptions.dnsServers', label: 'Custom DNS Server Count', description: 'Require custom DNS servers (not Azure-provided default)', suggestedName: 'Custom DNS Required on VNets', valueOptions: ['1', '2'] },
      { path: 'properties.virtualNetworkPeerings', label: 'VNet Peering Count Limit', description: 'Max number of VNet peering connections', suggestedName: 'VNet Peering Limit', valueOptions: ['5', '10', '25', '50'] },
      { path: 'properties.subnets.networkSecurityGroup', label: 'Subnet NSG Required', description: 'Require an NSG attached to every subnet', suggestedName: 'NSG Required on All Subnets', valueOptions: ['null'] },
      { path: 'properties.enableDdosProtection', label: 'DDoS Protection Enabled', description: 'Require DDoS Protection Standard on production VNets', suggestedName: 'DDoS Protection Required', valueOptions: ['true', 'false'] },
    ],
    'Microsoft.Storage/storageAccounts': [
      { path: 'properties.allowBlobPublicAccess', label: 'Allow Public Blob Access', description: 'Prevent public internet access to blob containers', suggestedName: 'No Public Blob Access', valueOptions: ['false', 'true'] },
      { path: 'properties.supportsHttpsTrafficOnly', label: 'HTTPS Traffic Only', description: 'Require HTTPS-only connections to storage', suggestedName: 'HTTPS Only on Storage', valueOptions: ['true', 'false'] },
      { path: 'properties.minimumTlsVersion', label: 'Minimum TLS Version', description: 'Enforce minimum TLS version for connections', suggestedName: 'Min TLS 1.2 on Storage', valueOptions: ['TLS1_2', 'TLS1_3', 'TLS1_1', 'TLS1_0'] },
      { path: 'properties.networkAcls.defaultAction', label: 'Default Network ACL Action', description: 'Block all traffic by default unless explicitly allowed', suggestedName: 'Storage Default Deny Network ACL', valueOptions: ['Deny', 'Allow'] },
    ],
    'Microsoft.Sql/servers': [
      { path: 'properties.firewallRules', label: 'Blocked Firewall IP Ranges', description: 'Block allow-all (0.0.0.0) firewall rules', suggestedName: 'No Open SQL Firewall', valueOptions: ['0.0.0.0', '0.0.0.0/0'] },
      { path: 'properties.transparentDataEncryption', label: 'Transparent Data Encryption (TDE)', description: 'Require TDE on all SQL databases', suggestedName: 'SQL Transparent Data Encryption', valueOptions: ['Enabled', 'Disabled'] },
      { path: 'properties.auditingPolicy.state', label: 'SQL Auditing State', description: 'Require SQL Server auditing to be enabled', suggestedName: 'SQL Auditing Required', valueOptions: ['Enabled', 'Disabled'] },
      { path: 'properties.tier', label: 'Service Tier Restriction', description: 'Restrict premium service tiers in dev/QA environments', suggestedName: 'No Premium SQL Tier', valueOptions: ['Basic', 'Standard', 'Premium', 'GeneralPurpose', 'BusinessCritical', 'Hyperscale'] },
      { path: 'properties.administrators.administratorType', label: 'Azure AD Admin Required', description: 'Require Azure AD administrator to be configured', suggestedName: 'Azure AD SQL Admin Required', valueOptions: ['ActiveDirectory'] },
    ],
    'Microsoft.KeyVault/vaults': [
      { path: 'properties.enableSoftDelete', label: 'Soft Delete Enabled', description: 'Prevent permanent deletion of keys, secrets, and certificates', suggestedName: 'Key Vault Soft Delete Required', valueOptions: ['true', 'false'] },
      { path: 'properties.enablePurgeProtection', label: 'Purge Protection Enabled', description: 'Prevent purge of soft-deleted vault items', suggestedName: 'Key Vault Purge Protection', valueOptions: ['true', 'false'] },
      { path: 'properties.accessPolicies', label: 'Max Access Policies Count', description: 'Limit number of access policies to reduce permission sprawl', suggestedName: 'Key Vault Access Policy Limit', valueOptions: ['5', '10', '15', '20'] },
      { path: 'properties.sku.name', label: 'SKU Tier', description: 'Restrict Key Vault SKU type', suggestedName: 'Key Vault SKU Restriction', valueOptions: ['standard', 'premium'] },
      { path: 'properties.networkAcls.defaultAction', label: 'Network ACL Default Action', description: 'Restrict network access by default', suggestedName: 'Key Vault Network Restriction', valueOptions: ['Deny', 'Allow'] },
    ],
    'Microsoft.Web/sites': [
      { path: 'properties.httpsOnly', label: 'HTTPS Only', description: 'Redirect all HTTP requests to HTTPS', suggestedName: 'HTTPS Only on App Services', valueOptions: ['true', 'false'] },
      { path: 'properties.siteConfig.minTlsVersion', label: 'Minimum TLS Version', description: 'Enforce minimum TLS version on App Services', suggestedName: 'Min TLS 1.2 on App Services', valueOptions: ['1.2', '1.3', '1.0', '1.1'] },
      { path: 'properties.siteConfig.http20Enabled', label: 'HTTP/2 Enabled', description: 'Require HTTP/2 for better performance and security', suggestedName: 'HTTP/2 Required on App Services', valueOptions: ['true', 'false'] },
      { path: 'properties.siteConfig.remoteDebuggingEnabled', label: 'Remote Debugging Disabled', description: 'Prevent remote debugging in production', suggestedName: 'No Remote Debugging in Production', valueOptions: ['false', 'true'] },
      { path: 'properties.siteConfig.scmIpSecurityRestrictionsUseMain', label: 'SCM IP Restrictions Enforced', description: 'Apply IP restrictions to the SCM (Kudu) site', suggestedName: 'SCM IP Restrictions Required', valueOptions: ['true', 'false'] },
    ],
    'Microsoft.ContainerService/managedClusters': [
      { path: 'properties.enableRBAC', label: 'Kubernetes RBAC Enabled', description: 'Require Kubernetes role-based access control on AKS', suggestedName: 'AKS RBAC Required', valueOptions: ['true', 'false'] },
      { path: 'properties.networkProfile.networkPolicy', label: 'Network Policy', description: 'Require network policy for pod-to-pod traffic control', suggestedName: 'AKS Network Policy Required', valueOptions: ['azure', 'calico', 'null'] },
      { path: 'properties.addonProfiles.azurePolicy.enabled', label: 'Azure Policy Add-on', description: 'Require the Azure Policy add-on for governance enforcement', suggestedName: 'AKS Azure Policy Add-on Required', valueOptions: ['true', 'false'] },
      { path: 'properties.apiServerAccessProfile.enablePrivateCluster', label: 'Private Cluster', description: 'Require AKS API server to be private (not internet-accessible)', suggestedName: 'AKS Private Cluster Required', valueOptions: ['true', 'false'] },
      { path: 'properties.agentPoolProfiles.osDiskSizeGB', label: 'Max Node OS Disk Size (GB)', description: 'Limit OS disk size per node in AKS node pools', suggestedName: 'AKS Node Disk Size Limit', valueOptions: ['100', '128', '256', '512'] },
    ],
  },
  aws: {
    '*': [
      { path: 'location', label: 'Region', description: 'AWS region where the resource is deployed', suggestedName: 'Approved Regions Only', valueOptions: ['us-east-1', 'us-west-2', 'eu-west-1', 'eu-central-1', 'ap-southeast-1', 'ap-northeast-1'] },
      { path: 'tags', label: 'Required Tag Keys', description: 'Enforce mandatory tag keys on all resources', suggestedName: 'Required Tags', valueOptions: ['Environment,Owner,CostCenter', 'Environment,Owner', 'CostCenter,BudgetCode,Department'] },
      { path: 'name', label: 'Resource Name / Naming Convention', description: 'Enforce a naming prefix or convention', suggestedName: 'Resource Naming Convention', valueOptions: ['prod-,dev-,stg-', 'use1-,usw2-,euw1-', 'platform-,app-,data-'] },
      { path: 'resource_group', label: 'Resource Count per Account', description: 'Limit resources per AWS account', suggestedName: 'Max Resources per Account', valueOptions: ['25', '50', '75', '100'] },
    ],
    'AWS::EC2::Instance': [
      { path: 'properties.instanceType', label: 'Instance Type', description: 'Restrict which EC2 instance types are allowed', suggestedName: 'Approved Instance Types', valueOptions: ['t3.micro', 't3.small', 't3.medium', 'm5.large', 'm5.xlarge', 'c5.large', 'r5.large'] },
      { path: 'properties.blockDeviceMappings.ebs.encrypted', label: 'EBS Encryption', description: 'Enforce encryption on EBS volumes', suggestedName: 'Require EBS Encryption', valueOptions: ['true'] },
      { path: 'properties.metadataOptions.httpTokens', label: 'IMDSv2 Required', description: 'Require Instance Metadata Service v2', suggestedName: 'IMDSv2 Required', valueOptions: ['required', 'optional'] },
      { path: 'properties.publicIpAddress', label: 'Public IP Address', description: 'Prevent instances from having public IPs', suggestedName: 'No Public IP on EC2', valueOptions: ['none', 'null'] },
      { path: 'properties.disableApiTermination', label: 'Termination Protection', description: 'Enable termination protection on production instances', suggestedName: 'Termination Protection Required', valueOptions: ['true', 'false'] },
    ],
    'AWS::S3::Bucket': [
      { path: 'properties.publicAccessBlockConfiguration.blockPublicAccess', label: 'Block Public Access', description: 'Prevent public access to S3 bucket', suggestedName: 'No Public S3 Access', valueOptions: ['true'] },
      { path: 'properties.serverSideEncryptionConfiguration', label: 'Server-Side Encryption', description: 'Require server-side encryption on S3 bucket', suggestedName: 'S3 Encryption Required', valueOptions: ['AES256', 'aws:kms'] },
      { path: 'properties.versioningConfiguration.status', label: 'Versioning', description: 'Enable versioning for data protection', suggestedName: 'S3 Versioning Required', valueOptions: ['Enabled', 'Suspended'] },
      { path: 'properties.bucketPolicy.sslOnly', label: 'SSL/TLS Only', description: 'Enforce HTTPS-only access via bucket policy', suggestedName: 'S3 SSL Only', valueOptions: ['true'] },
    ],
    'AWS::EC2::SecurityGroup': [
      { path: 'properties.ipPermissions', label: 'Max Inbound Rules', description: 'Maximum number of inbound rules per Security Group', suggestedName: 'Security Group Rule Limit', valueOptions: ['5', '10', '15', '20'] },
      { path: 'properties.ipPermissions.cidrIp', label: 'Block Allow-All Source', description: 'Prevent rules allowing traffic from any source', suggestedName: 'No Allow-All Inbound Rules', valueOptions: ['0.0.0.0/0'] },
      { path: 'properties.ipPermissions.dangerousPorts', label: 'Blocked Ports from Internet', description: 'Block SSH / RDP access from internet', suggestedName: 'Block SSH and RDP from Internet', valueOptions: ['22,3389', '22,23,3389'] },
    ],
    'AWS::EC2::VPC': [
      { path: 'properties.flowLogs.enabled', label: 'VPC Flow Logs', description: 'Enable VPC Flow Logs for traffic monitoring', suggestedName: 'VPC Flow Logs Required', valueOptions: ['true'] },
      { path: 'properties.vpcPeeringConnections', label: 'VPC Peering Limit', description: 'Max number of VPC peering connections', suggestedName: 'VPC Peering Limit', valueOptions: ['5', '10', '25'] },
    ],
    'AWS::RDS::DBInstance': [
      { path: 'properties.publiclyAccessible', label: 'Public Access', description: 'Prevent RDS instances from being publicly accessible', suggestedName: 'No Public RDS Access', valueOptions: ['false'] },
      { path: 'properties.storageEncrypted', label: 'Storage Encryption', description: 'Require encryption at rest for RDS', suggestedName: 'RDS Encryption Required', valueOptions: ['true'] },
      { path: 'properties.multiAZ', label: 'Multi-AZ Deployment', description: 'Require Multi-AZ for high availability', suggestedName: 'RDS Multi-AZ Required', valueOptions: ['true'] },
      { path: 'properties.backupRetentionPeriod', label: 'Backup Retention (days)', description: 'Minimum backup retention period', suggestedName: 'RDS Backup Retention', valueOptions: ['7', '14', '30'] },
    ],
    'AWS::Lambda::Function': [
      { path: 'properties.runtime', label: 'Runtime Version', description: 'Restrict to approved Lambda runtimes', suggestedName: 'Approved Lambda Runtimes', valueOptions: ['python3.12', 'python3.11', 'nodejs20.x', 'nodejs18.x', 'java21'] },
      { path: 'properties.vpcConfig.vpcId', label: 'VPC Configuration', description: 'Require Lambda functions to run in a VPC', suggestedName: 'Lambda VPC Required', valueOptions: ['required'] },
    ],
    'AWS::IAM::User': [
      { path: 'properties.mfaDevices', label: 'MFA Devices', description: 'Require MFA to be configured for IAM users', suggestedName: 'IAM MFA Required', valueOptions: ['1'] },
      { path: 'properties.accessKeyAge', label: 'Access Key Age (days)', description: 'Maximum age for IAM access keys', suggestedName: 'Access Key Rotation', valueOptions: ['30', '60', '90'] },
    ],
    'AWS::EKS::Cluster': [
      { path: 'properties.resourcesVpcConfig.endpointPrivateAccess', label: 'Private API Endpoint', description: 'Require private API server endpoint', suggestedName: 'EKS Private Endpoint Required', valueOptions: ['true'] },
    ],
  },
  gcp: {
    '*': [
      { path: 'location', label: 'Region / Zone', description: 'GCP region or zone where the resource is deployed', suggestedName: 'Approved Regions Only', valueOptions: ['us-central1', 'us-east1', 'europe-west1', 'europe-west4', 'asia-east1', 'asia-southeast1'] },
      { path: 'tags', label: 'Required Label Keys', description: 'Enforce mandatory labels on all resources', suggestedName: 'Required Labels', valueOptions: ['environment,owner,cost-center', 'environment,owner', 'cost-center,budget-code,department'] },
      { path: 'name', label: 'Resource Name / Naming Convention', description: 'Enforce a naming prefix or convention', suggestedName: 'Resource Naming Convention', valueOptions: ['prod-,dev-,stg-', 'usc1-,use1-,euw1-', 'platform-,app-,data-'] },
      { path: 'resource_group', label: 'Resource Count per Project', description: 'Limit resources per GCP project', suggestedName: 'Max Resources per Project', valueOptions: ['25', '50', '75', '100'] },
    ],
    'compute.googleapis.com/Instance': [
      { path: 'properties.machineType', label: 'Machine Type', description: 'Restrict which machine types are allowed', suggestedName: 'Approved Machine Types', valueOptions: ['e2-micro', 'e2-small', 'e2-medium', 'n2-standard-2', 'n2-standard-4', 'n2-standard-8'] },
      { path: 'properties.disks.diskEncryptionKey', label: 'Disk Encryption (CMEK)', description: 'Enforce Customer-Managed Encryption Keys on disks', suggestedName: 'Require CMEK Disk Encryption', valueOptions: ['required'] },
      { path: 'properties.networkInterfaces.accessConfigs', label: 'External IP Address', description: 'Prevent VMs from having external IPs', suggestedName: 'No External IP on VMs', valueOptions: ['none'] },
      { path: 'properties.deletionProtection', label: 'Deletion Protection', description: 'Enable deletion protection on production VMs', suggestedName: 'Deletion Protection Required', valueOptions: ['true'] },
      { path: 'properties.metadata.enable-oslogin', label: 'OS Login', description: 'Require OS Login for centralized SSH management', suggestedName: 'OS Login Required', valueOptions: ['TRUE'] },
    ],
    'storage.googleapis.com/Bucket': [
      { path: 'properties.iamConfiguration.uniformBucketLevelAccess.enabled', label: 'Uniform Bucket-Level Access', description: 'Enforce uniform access control (disable ACLs)', suggestedName: 'Uniform Bucket Access Required', valueOptions: ['true'] },
      { path: 'properties.encryption.defaultKmsKeyName', label: 'Encryption (CMEK)', description: 'Require Customer-Managed Encryption Key', suggestedName: 'GCS CMEK Required', valueOptions: ['required'] },
      { path: 'properties.versioning.enabled', label: 'Object Versioning', description: 'Enable object versioning for data protection', suggestedName: 'GCS Versioning Required', valueOptions: ['true'] },
      { path: 'properties.logging.logBucket', label: 'Access Logging', description: 'Enable access logging for audit', suggestedName: 'GCS Logging Required', valueOptions: ['required'] },
    ],
    'compute.googleapis.com/Network': [
      { path: 'properties.firewallRules', label: 'Max Firewall Rules', description: 'Maximum number of firewall rules per VPC', suggestedName: 'Firewall Rule Limit', valueOptions: ['5', '10', '15', '20'] },
      { path: 'properties.firewallRules.sourceRanges', label: 'Block Allow-All Source', description: 'Prevent rules allowing traffic from 0.0.0.0/0', suggestedName: 'No Allow-All Ingress', valueOptions: ['0.0.0.0/0'] },
      { path: 'properties.firewallRules.dangerousPorts', label: 'Blocked Ports', description: 'Block SSH / RDP from internet', suggestedName: 'Block SSH and RDP', valueOptions: ['22,3389', '22,23,3389'] },
      { path: 'properties.subnetworks.logConfig.enable', label: 'VPC Flow Logs', description: 'Enable VPC Flow Logs for monitoring', suggestedName: 'VPC Flow Logs Required', valueOptions: ['true'] },
      { path: 'properties.peerings', label: 'VPC Peering Limit', description: 'Max number of VPC peering connections', suggestedName: 'VPC Peering Limit', valueOptions: ['5', '10', '25'] },
    ],
    'sqladmin.googleapis.com/Instance': [
      { path: 'properties.settings.ipConfiguration.ipv4Enabled', label: 'Public IP', description: 'Prevent Cloud SQL from having public IP', suggestedName: 'No Public IP on Cloud SQL', valueOptions: ['false'] },
      { path: 'properties.diskEncryptionConfiguration.kmsKeyName', label: 'Encryption (CMEK)', description: 'Require Customer-Managed Encryption Key', suggestedName: 'Cloud SQL CMEK Required', valueOptions: ['required'] },
      { path: 'properties.settings.availabilityType', label: 'High Availability', description: 'Require regional (HA) configuration', suggestedName: 'Cloud SQL HA Required', valueOptions: ['REGIONAL', 'ZONAL'] },
      { path: 'properties.settings.backupConfiguration.enabled', label: 'Automated Backups', description: 'Require automated backups', suggestedName: 'Cloud SQL Backup Required', valueOptions: ['true'] },
    ],
    'cloudfunctions.googleapis.com/Function': [
      { path: 'properties.runtime', label: 'Runtime Version', description: 'Restrict to approved Cloud Function runtimes', suggestedName: 'Approved Cloud Function Runtimes', valueOptions: ['python312', 'python311', 'nodejs20', 'nodejs18', 'java17'] },
    ],
    'iam.googleapis.com/ServiceAccount': [
      { path: 'properties.keys.validAfterTime', label: 'Key Age (days)', description: 'Maximum age for service account keys', suggestedName: 'SA Key Rotation', valueOptions: ['30', '60', '90'] },
    ],
    'container.googleapis.com/Cluster': [
      { path: 'properties.privateClusterConfig.enablePrivateNodes', label: 'Private Nodes', description: 'Require private nodes (no external IPs)', suggestedName: 'GKE Private Nodes Required', valueOptions: ['true'] },
      { path: 'properties.networkPolicy.enabled', label: 'Network Policy', description: 'Require network policy enforcement', suggestedName: 'GKE Network Policy Required', valueOptions: ['true'] },
    ],
  },
};

function evaluateCompliancePosture(resourceDrifts, frameworks) {
  const allChanges = [];
  resourceDrifts.forEach(rd => {
    (rd.changes || []).forEach(c => {
      allChanges.push({ ...c, resource_name: rd.resource_name, resource_type: rd.resource_type || '', resource_group: rd.resource_group || '' });
    });
  });

  const hasDriftData = allChanges.length > 0;

  const results = {};
  Object.entries(frameworks).forEach(([fwName, fw]) => {
    const controlResults = fw.controls.map(ctrl => {
      const isEvaluable = ctrl.driftMatch !== noMatch;
      if (!isEvaluable) {
        return { ...ctrl, status: 'not_evaluated', affectedResources: [], affectedCount: 0 };
      }
      const matchingResources = allChanges.filter(c => ctrl.driftMatch(c));
      let status;
      if (matchingResources.length > 0) {
        status = 'at_risk';
      } else if (hasDriftData) {
        status = 'compliant';
      } else {
        status = 'not_evaluated';
      }
      return { ...ctrl, status, affectedResources: matchingResources, affectedCount: matchingResources.length };
    });
    const atRisk = controlResults.filter(c => c.status === 'at_risk').length;
    const compliant = controlResults.filter(c => c.status === 'compliant').length;
    const notEvaluated = controlResults.filter(c => c.status === 'not_evaluated').length;
    const evaluated = controlResults.filter(c => c.status !== 'not_evaluated').length;
    const total = controlResults.length;
    const compliancePercent = total > 0 ? Math.round((compliant / total) * 100) : 0;
    results[fwName] = {
      ...fw,
      controls: controlResults,
      atRisk,
      compliant,
      notEvaluated,
      evaluated,
      total,
      compliancePercent,
    };
  });
  return results;
}

function CustomRuleBuilder({ existingRules, onClose, onAdd, cloudProvider = 'azure' }) {
  const [step, setStep] = useState(1);
  const [category, setCategory] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [operator, setOperator] = useState('equals');
  const [propertyPath, setPropertyPath] = useState('');
  const [expectedValue, setExpectedValue] = useState('');
  const [ruleName, setRuleName] = useState('');
  const [severity, setSeverity] = useState('high');
  const [scope, setScope] = useState('*');
  const [adding, setAdding] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!ruleName.trim()) e.ruleName = 'Rule name is required';
    if (!resourceType) e.resourceType = 'Select a resource type';
    if (!propertyPath.trim()) e.propertyPath = 'Property path is required';
    if (!expectedValue.trim()) e.expectedValue = 'Expected value is required';
    if (!category) e.category = 'Select a category';
    if (existingRules.some(r => r.name.toLowerCase() === ruleName.trim().toLowerCase())) {
      e.ruleName = 'A rule with this name already exists';
    }
    if (isDuplicateRule(existingRules, { target_resource_type: resourceType, property_path: propertyPath, operator, expected_value: expectedValue, name: ruleName })) {
      e.duplicate = 'A rule with the same logic already exists in this baseline';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setAdding(true);
    try {
      await onAdd({
        name: ruleName,
        rule_type: 'property_match',
        target_resource_type: resourceType,
        target_scope: scope,
        property_path: propertyPath,
        operator,
        expected_value: expectedValue,
        severity,
      });
      onClose();
    } catch (e) {
      setErrors({ submit: 'Failed to add rule. Please try again.' });
    }
    setAdding(false);
  };

  const stepTitles = ['Category', 'Resource Type', 'Condition', 'Parameter', 'Severity', 'Scope & Confirm'];

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 600,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto'
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: '1.25rem 1.5rem', background: 'linear-gradient(135deg, #f5f3ff, #faf5ff)',
          borderBottom: '1px solid #ddd6fe', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: '#5b21b6' }}>Create Custom Rule</div>
            <div style={{ fontSize: '0.75rem', color: '#7c3aed' }}>Step {step} of 6: {stepTitles[step - 1]}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ display: 'flex', padding: '0 1.5rem', paddingTop: '1rem', gap: '0.25rem' }}>
          {[1,2,3,4,5,6].map(s => (
            <div key={s} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: s <= step ? '#7c3aed' : '#e5e7eb',
              transition: 'background 0.2s'
            }} />
          ))}
        </div>

        <div style={{ padding: '1.25rem 1.5rem' }}>
          {step === 1 && (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>Select a category for this rule:</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                {Object.entries(RULE_CATEGORIES_BY_PROVIDER[cloudProvider] || RULE_CATEGORIES_BY_PROVIDER.azure).map(([cat, info]) => (
                  <div key={cat} onClick={() => { setCategory(cat); setStep(2); }} style={{
                    padding: '0.85rem', borderRadius: 10, cursor: 'pointer',
                    border: `2px solid ${category === cat ? info.color : '#e5e7eb'}`,
                    background: category === cat ? `${info.color}08` : 'white',
                    transition: 'all 0.15s'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '1.1rem' }}>{info.icon}</span>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>{cat}</span>
                    </div>
                  </div>
                ))}
              </div>
              {errors.category && <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.category}</div>}
            </div>
          )}

          {step === 2 && (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>Select the target resource type:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {(RESOURCE_TYPES_BY_PROVIDER[cloudProvider] || RESOURCE_TYPES_BY_PROVIDER.azure).map(rt => (
                  <div key={rt.value} onClick={() => { setResourceType(rt.value); setStep(3); }} style={{
                    padding: '0.7rem 1rem', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${resourceType === rt.value ? '#7c3aed' : '#e5e7eb'}`,
                    background: resourceType === rt.value ? '#f5f3ff' : 'white',
                    fontSize: '0.85rem', fontWeight: 500, color: '#374151'
                  }}>
                    {rt.label}
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 8 }}>{rt.value}</span>
                  </div>
                ))}
              </div>
              {errors.resourceType && <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '0.5rem' }}>{errors.resourceType}</div>}
            </div>
          )}

          {step === 3 && (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>Select the condition type:</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                {Object.entries(OPERATOR_LABELS).map(([op, label]) => (
                  <div key={op} onClick={() => { setOperator(op); setStep(4); }} style={{
                    padding: '0.7rem 1rem', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${operator === op ? '#7c3aed' : '#e5e7eb'}`,
                    background: operator === op ? '#f5f3ff' : 'white',
                    fontSize: '0.82rem', fontWeight: 500, color: '#374151'
                  }}>{label}</div>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (() => {
            const providerProps = PROPERTY_OPTIONS_BY_PROVIDER[cloudProvider] || PROPERTY_OPTIONS_BY_PROVIDER.azure;
            const typeOptions = [
              ...(providerProps['*'] || []),
              ...(providerProps[resourceType] || [])
            ];
            const selectedProp = typeOptions.find(p => p.path === propertyPath);
            return (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                Pick a property to monitor, then set the expected value:
              </p>

              <div style={{ marginBottom: '0.85rem' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, marginBottom: '0.4rem', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  1. What property do you want to check?
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.4rem' }}>
                  {typeOptions.map(opt => (
                    <div key={opt.path}
                      onClick={() => {
                        setPropertyPath(opt.path);
                        setErrors(p => ({...p, propertyPath: null}));
                        if (!ruleName || ruleName === selectedProp?.suggestedName) setRuleName(opt.suggestedName);
                        if (opt.valueOptions.length === 1) { setExpectedValue(opt.valueOptions[0]); }
                        else if (expectedValue && !opt.valueOptions.includes(expectedValue)) setExpectedValue('');
                      }}
                      style={{
                        padding: '0.55rem 0.75rem', borderRadius: 6, cursor: 'pointer',
                        border: `2px solid ${propertyPath === opt.path ? '#7c3aed' : '#e5e7eb'}`,
                        background: propertyPath === opt.path ? '#f5f3ff' : 'white',
                        transition: 'all 0.12s'
                      }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: propertyPath === opt.path ? '#5b21b6' : '#111' }}>{opt.label}</div>
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 1 }}>{opt.description}</div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>{opt.path}</div>
                    </div>
                  ))}
                </div>
                {errors.propertyPath && <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 3 }}>{errors.propertyPath}</div>}
              </div>

              {propertyPath && (
                <>
                  <div style={{ marginBottom: '0.85rem' }}>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, marginBottom: '0.4rem', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      2. Expected value ({OPERATOR_LABELS[operator]})
                    </label>
                    {selectedProp?.valueOptions?.length > 0 && (
                      <>
                        <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: '0.4rem' }}>
                          Click a preset or type a custom value:
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
                          {selectedProp.valueOptions.map(v => (
                            <button key={v} onClick={() => { setExpectedValue(v); setErrors(p => ({...p, expectedValue: null})); }}
                              style={{
                                padding: '0.3rem 0.65rem', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                                border: `2px solid ${expectedValue === v ? '#7c3aed' : '#e5e7eb'}`,
                                background: expectedValue === v ? '#f5f3ff' : 'white',
                                color: expectedValue === v ? '#5b21b6' : '#374151'
                              }}>{v}</button>
                          ))}
                        </div>
                      </>
                    )}
                    <input value={expectedValue}
                      onChange={e => { setExpectedValue(e.target.value); setErrors(p => ({...p, expectedValue: null})); }}
                      placeholder={['in', 'not_in', 'contains_keys', 'contains'].includes(operator) ? 'value1, value2, value3  (comma-separated)' : 'Type a custom value...'}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: `1px solid ${errors.expectedValue ? '#dc2626' : '#d1d5db'}`, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                    {['in', 'not_in', 'contains_keys', 'contains'].includes(operator) && (
                      <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 3 }}>Separate multiple values with commas</div>
                    )}
                    {errors.expectedValue && <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 3 }}>{errors.expectedValue}</div>}
                  </div>

                  <div style={{ marginBottom: '0.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, marginBottom: '0.4rem', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                      3. Rule name
                    </label>
                    <input value={ruleName} onChange={e => { setRuleName(e.target.value); setErrors(p => ({...p, ruleName: null})); }}
                      placeholder="Give this rule a descriptive name"
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: `1px solid ${errors.ruleName ? '#dc2626' : '#d1d5db'}`, fontSize: '0.85rem', boxSizing: 'border-box' }} />
                    {errors.ruleName && <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: 3 }}>{errors.ruleName}</div>}
                  </div>
                </>
              )}
            </div>
            );
          })()}

          {step === 5 && (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>Select the severity level:</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {['critical', 'high', 'medium', 'low'].map(s => (
                  <div key={s} onClick={() => { setSeverity(s); setStep(6); }} style={{
                    padding: '0.75rem 1rem', borderRadius: 8, cursor: 'pointer',
                    border: `2px solid ${severity === s ? SEVERITY_COLORS[s] : '#e5e7eb'}`,
                    background: severity === s ? SEVERITY_BG[s] : 'white',
                    display: 'flex', alignItems: 'center', gap: '0.75rem'
                  }}>
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: SEVERITY_COLORS[s]
                    }} />
                    <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#374151', textTransform: 'capitalize' }}>{s}</span>
                    <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>
                      {s === 'critical' ? 'Weight: 8x' : s === 'high' ? 'Weight: 5x' : s === 'medium' ? 'Weight: 3x' : 'Weight: 1x'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', marginBottom: '1rem' }}>Select scope and review your rule:</p>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Scope</label>
                <select value={scope} onChange={e => setScope(e.target.value)}
                  style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                  <option value="*">All Resources</option>
                  <option value="subscription">Subscription Level</option>
                  <option value="rg-prod">Production Resource Groups</option>
                  <option value="rg-dev">Development Resource Groups</option>
                </select>
              </div>

              <div style={{
                padding: '1rem', borderRadius: 10, background: '#f9fafb', border: '1px solid #e5e7eb',
                marginBottom: '1rem'
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Rule Summary</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', fontSize: '0.82rem' }}>
                  <div><span style={{ color: '#9ca3af' }}>Name:</span> <strong>{ruleName || '—'}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Category:</span> <strong>{category || '—'}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Resource:</span> <strong>{(RESOURCE_TYPES_BY_PROVIDER[cloudProvider] || RESOURCE_TYPES_BY_PROVIDER.azure).find(r => r.value === resourceType)?.label || resourceType || '—'}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Field:</span> <strong>{propertyPath || '—'}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Operator:</span> <strong>{OPERATOR_LABELS[operator]}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Value:</span> <strong>{expectedValue || '—'}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Severity:</span> <strong style={{ color: SEVERITY_COLORS[severity], textTransform: 'capitalize' }}>{severity}</strong></div>
                  <div><span style={{ color: '#9ca3af' }}>Scope:</span> <strong>{scope === '*' ? 'All' : scope}</strong></div>
                </div>
              </div>

              {errors.duplicate && (
                <div style={{ padding: '0.5rem 0.75rem', borderRadius: 6, background: '#fffbeb', border: '1px solid #fde68a', fontSize: '0.78rem', color: '#92400e', marginBottom: '0.75rem' }}>
                  <FiAlertTriangle size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} /> {errors.duplicate}
                </div>
              )}
              {errors.submit && (
                <div style={{ padding: '0.5rem 0.75rem', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', fontSize: '0.78rem', color: '#991b1b', marginBottom: '0.75rem' }}>{errors.submit}</div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.25rem' }}>
            <button onClick={() => step > 1 ? setStep(step - 1) : onClose()} style={{
              padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer'
            }}>{step === 1 ? 'Cancel' : 'Back'}</button>
            {step < 6 ? (
              <button onClick={() => setStep(step + 1)} disabled={
                (step === 1 && !category) || (step === 2 && !resourceType)
              } style={{
                padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
                border: 'none', background: '#7c3aed', color: 'white', cursor: 'pointer',
                opacity: ((step === 1 && !category) || (step === 2 && !resourceType)) ? 0.5 : 1
              }}>Next</button>
            ) : (
              <button onClick={handleSubmit} disabled={adding} style={{
                padding: '0.5rem 1.5rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700,
                border: 'none', background: '#7c3aed', color: 'white',
                cursor: adding ? 'not-allowed' : 'pointer', opacity: adding ? 0.6 : 1
              }}>{adding ? 'Adding...' : 'Create Rule'}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EditRuleModal({ rule, onClose, onSave }) {
  const [severity, setSeverity] = useState(rule.severity);
  const [expectedValue, setExpectedValue] = useState(rule.expected_value);
  const [scope, setScope] = useState(rule.target_scope || '*');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!expectedValue.trim()) {
      setError('Expected value cannot be empty');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(rule.id, {
        name: rule.name,
        rule_type: rule.rule_type,
        target_resource_type: rule.target_resource_type,
        target_scope: scope,
        property_path: rule.property_path,
        operator: rule.operator,
        expected_value: expectedValue,
        severity
      });
      onClose();
    } catch (e) {
      setError('Failed to update rule. Please try again.');
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }} onClick={onClose}>
      <div style={{
        background: 'white', borderRadius: 16, width: '100%', maxWidth: 460,
        boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: '#374151' }}>Edit Rule: {rule.name}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={18} /></button>
        </div>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Severity</label>
              <select value={severity} onChange={e => setSeverity(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Expected Value</label>
              <input value={expectedValue} onChange={e => setExpectedValue(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Scope</label>
              <select value={scope} onChange={e => setScope(e.target.value)}
                style={{ width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                <option value="*">All Resources</option>
                <option value="subscription">Subscription Level</option>
                <option value="rg-prod">Production Resource Groups</option>
                <option value="rg-dev">Development Resource Groups</option>
              </select>
            </div>
          </div>
          {error && (
            <div style={{
              padding: '0.5rem 0.75rem', borderRadius: 6, background: '#fef2f2',
              border: '1px solid #fecaca', fontSize: '0.78rem', color: '#991b1b', marginBottom: '0.75rem'
            }}>{error}</div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{
              padding: '0.5rem 1rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer'
            }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '0.5rem 1.25rem', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600,
              border: 'none', background: '#2563eb', color: 'white', cursor: 'pointer',
              opacity: saving ? 0.6 : 1
            }}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DriftMonitoringContent() {
  const { selectedTenantId, tenants } = useTenant();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [baselines, setBaselines] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedResources, setExpandedResources] = useState({});
  const [showBaselineForm, setShowBaselineForm] = useState(false);
  const [baselineName, setBaselineName] = useState('');
  const [baselineDesc, setBaselineDesc] = useState('');
  const [settingBaseline, setSettingBaseline] = useState(false);
  const [baselineMsg, setBaselineMsg] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [syncing, setSyncing] = useState(false);
  const [showFormula, setShowFormula] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('compliance');
  const [expandedFramework, setExpandedFramework] = useState(null);
  const [complianceViewMode, setComplianceViewMode] = useState('all');
  const [templates, setTemplates] = useState([]);
  const [baselineRules, setBaselineRules] = useState([]);
  const [selectedBaseline, setSelectedBaseline] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCustomRule, setShowCustomRule] = useState(false);
  const [ruleSearch, setRuleSearch] = useState('');
  const [ruleCategoryFilter, setRuleCategoryFilter] = useState('all');
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [templateToConfig, setTemplateToConfig] = useState(null);
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState('all');
  const [editingRule, setEditingRule] = useState(null);
  const [ruleDeleteConfirm, setRuleDeleteConfirm] = useState(null);
  const [compliancePosture, setCompliancePosture] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState(null);
  const [cloudPolicies, setCloudPolicies] = useState(null);
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policySearch, setPolicySearch] = useState('');
  const [policyCategoryFilter, setPolicyCategoryFilter] = useState('all');
  const [policyEffectFilter, setPolicyEffectFilter] = useState('all');
  const [expandedPolicy, setExpandedPolicy] = useState(null);
  const [expandedControl, setExpandedControl] = useState(null);
  const [controlResources, setControlResources] = useState({});
  const [controlResourcesLoading, setControlResourcesLoading] = useState({});
  const [expandedPolicyRow, setExpandedPolicyRow] = useState(null);
  const [aiGuide, setAiGuide] = useState(null);
  const [aiLoading, setAiLoading] = useState(null);

  const fetchData = useCallback(() => {
    if (!selectedTenantId) return;
    setLoading(true);
    setError(null);
    setCloudPolicies(null);
    setCompliancePosture(null);
    setComplianceError(null);
    setPolicySearch('');
    setPolicyCategoryFilter('all');
    setPolicyEffectFilter('all');
    setExpandedPolicy(null);
    const cp = (tenants?.find(t => String(t.id) === String(selectedTenantId))?.cloud_provider || 'azure').toLowerCase();
    Promise.all([
      getDriftReport(selectedTenantId),
      getBaselines(selectedTenantId),
      getRuleTemplates(cp)
    ])
      .then(([reportRes, baselinesRes, templatesRes]) => {
        setData(reportRes.data);
        const bl = baselinesRes.data.baselines || [];
        setBaselines(bl);
        setTemplates(templatesRes.data.templates || []);
        const active = bl.find(b => b.is_active);
        if (active) {
          setSelectedBaseline(active);
          getBaselineRules(active.id).then(r => setBaselineRules(r.data.rules || [])).catch(() => {});
        }
        setLoading(false);
      })
      .catch(e => {
        const d = e.response?.data?.detail;
        setError(typeof d === 'string' ? d : 'Failed to load drift report');
        setLoading(false);
      });
  }, [selectedTenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'policies' && selectedTenantId && !cloudPolicies && !policiesLoading) {
      setPoliciesLoading(true);
      getCloudPolicies(selectedTenantId)
        .then(r => { setCloudPolicies(r.data); setPoliciesLoading(false); })
        .catch(() => setPoliciesLoading(false));
    }
  }, [activeTab, selectedTenantId, cloudPolicies, policiesLoading]);

  useEffect(() => {
    if (activeTab === 'compliance' && selectedTenantId && !compliancePosture && !complianceLoading) {
      setComplianceLoading(true);
      setComplianceError(null);
      getCompliancePosture(selectedTenantId)
        .then(r => { setCompliancePosture(r.data); setComplianceLoading(false); })
        .catch(e => {
          setComplianceError(e.response?.data?.detail || 'Failed to load compliance data');
          setComplianceLoading(false);
        });
    }
  }, [activeTab, selectedTenantId, compliancePosture, complianceLoading]);

  const refreshBaselines = async () => {
    const res = await getBaselines(selectedTenantId);
    const bl = res.data.baselines || [];
    setBaselines(bl);
    return bl;
  };

  const refreshRules = async (baselineId) => {
    const res = await getBaselineRules(baselineId);
    setBaselineRules(res.data.rules || []);
  };

  const handleExpandControl = async (stdName, ctrlId) => {
    const key = `${stdName}::${ctrlId}`;
    if (expandedControl === key) {
      setExpandedControl(null);
      return;
    }
    setExpandedControl(key);
    if (!controlResources[key]) {
      setControlResourcesLoading(prev => ({ ...prev, [key]: true }));
      try {
        const res = await getControlResources(selectedTenantId, stdName, ctrlId);
        setControlResources(prev => ({ ...prev, [key]: res.data.resources || [] }));
      } catch (e) {
        setControlResources(prev => ({ ...prev, [key]: [] }));
      }
      setControlResourcesLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAskAI = async (itemType, itemName, itemDescription, category, complianceStatus, severity, affectedResources) => {
    const loadKey = `${itemType}::${itemName}`;
    setAiLoading(loadKey);
    try {
      const res = await getAIPolicyRemediation({
        tenant_id: selectedTenantId,
        item_type: itemType,
        item_name: itemName,
        item_description: itemDescription || '',
        category: category || '',
        compliance_status: complianceStatus || '',
        severity: severity || '',
        affected_resources: (affectedResources || []).slice(0, 10).map(r => typeof r === 'string' ? r : (r.resource_name || r.resource_id || 'Unknown')),
        cloud_provider: cloudProvider,
      });
      setAiGuide(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || 'AI service encountered an error. Please try again.';
      alert(detail);
    }
    setAiLoading(null);
  };

  const handleSetBaseline = async () => {
    if (!baselineName.trim()) return;
    setSettingBaseline(true);
    setBaselineMsg(null);
    try {
      const res = await setBaseline(selectedTenantId, { name: baselineName, description: baselineDesc });
      setBaselineMsg({ type: 'success', text: `Baseline "${baselineName}" created with ${res.data.baseline?.resource_count || 0} resources. Now add rules or re-sync to detect drift.` });
      setBaselineName('');
      setBaselineDesc('');
      const bl = await refreshBaselines();
      const newActive = bl.find(b => b.is_active);
      if (newActive) {
        setSelectedBaseline(newActive);
        await refreshRules(newActive.id);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      setBaselineMsg({ type: 'error', text: typeof d === 'string' ? d : 'Failed to set baseline' });
    }
    setSettingBaseline(false);
  };

  const handleActivateBaseline = async (baselineId) => {
    try {
      await activateBaseline(selectedTenantId, baselineId);
      const bl = await refreshBaselines();
      const newActive = bl.find(b => b.is_active);
      if (newActive) {
        setSelectedBaseline(newActive);
        await refreshRules(newActive.id);
      }
    } catch (e) {}
  };

  const handleDeleteBaseline = async (baselineId) => {
    try {
      await deleteBaseline(selectedTenantId, baselineId);
      setDeleteConfirm(null);
      const bl = await refreshBaselines();
      const newActive = bl.find(b => b.is_active);
      if (newActive) {
        setSelectedBaseline(newActive);
        await refreshRules(newActive.id);
      } else {
        setSelectedBaseline(null);
        setBaselineRules([]);
      }
    } catch (e) {}
  };

  const handleResync = async () => {
    setSyncing(true);
    try {
      await syncTenant(selectedTenantId);
      fetchData();
    } catch (e) {}
    setSyncing(false);
  };

  const handleAddRuleFromTemplate = async (ruleData) => {
    if (!selectedBaseline) return;
    await addBaselineRule(selectedBaseline.id, ruleData);
    await refreshRules(selectedBaseline.id);
    await refreshBaselines();
  };

  const handleAddCustomRule = async (ruleData) => {
    if (!selectedBaseline) return;
    await addBaselineRule(selectedBaseline.id, ruleData);
    await refreshRules(selectedBaseline.id);
    await refreshBaselines();
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await deleteRule(ruleId);
      setRuleDeleteConfirm(null);
      if (selectedBaseline) {
        await refreshRules(selectedBaseline.id);
        await refreshBaselines();
      }
      if (selectedTenantId) {
        getDriftReport(selectedTenantId).then(r => setData(r.data)).catch(() => {});
      }
    } catch (e) {}
  };

  const handleToggleRule = async (ruleId) => {
    try {
      await toggleRule(ruleId);
      if (selectedBaseline) await refreshRules(selectedBaseline.id);
      if (selectedTenantId) {
        getDriftReport(selectedTenantId).then(r => setData(r.data)).catch(() => {});
      }
    } catch (e) {}
  };

  const handleUpdateRule = async (ruleId, ruleData) => {
    await updateRule(ruleId, ruleData);
    if (selectedBaseline) await refreshRules(selectedBaseline.id);
  };

  const handleSelectBaseline = async (b) => {
    setSelectedBaseline(b);
    await refreshRules(b.id);
  };

  const selectedTenant = tenants?.find(t => String(t.id) === String(selectedTenantId) || t.tenant_id === selectedTenantId);
  const cloudProvider = selectedTenant?.cloud_provider || 'azure';

  const groupedRules = useMemo(() => {
    const groups = {};
    const search = ruleSearch.toLowerCase();
    const filtered = baselineRules.filter(r => {
      if (search && !r.name.toLowerCase().includes(search) && !r.property_path.toLowerCase().includes(search)) return false;
      if (ruleCategoryFilter !== 'all') {
        const cat = categorizeRule(r, cloudProvider);
        if (cat !== ruleCategoryFilter) return false;
      }
      return true;
    });
    filtered.forEach(r => {
      const cat = categorizeRule(r, cloudProvider);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(r);
    });
    return groups;
  }, [baselineRules, ruleSearch, ruleCategoryFilter, cloudProvider]);

  const categoryCounts = useMemo(() => {
    const counts = {};
    baselineRules.forEach(r => {
      const cat = categorizeRule(r, cloudProvider);
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [baselineRules, cloudProvider]);

  const templateAlreadyAdded = useCallback((tmpl) => {
    return baselineRules.some(r =>
      r.name.toLowerCase() === tmpl.name.toLowerCase() ||
      (r.target_resource_type === tmpl.target_resource_type &&
       r.property_path === tmpl.property_path &&
       r.operator === tmpl.operator)
    );
  }, [baselineRules]);

  const resourceDrifts = useMemo(() => data?.resource_drifts || [], [data]);
  const frameworks = useMemo(() => getFrameworksForProvider(cloudProvider), [cloudProvider]);
  const complianceMemo = useMemo(() => evaluateCompliancePosture(resourceDrifts, frameworks), [resourceDrifts, frameworks]);

  if (loading) return <div className="loading-container"><div className="spinner" /><p>Loading drift report...</p></div>;
  if (error) return <div className="error-container"><p>{error}</p></div>;

  const score = data?.drift_score ?? 0;
  const baseline = data?.baseline;
  const trend = data?.trend || {};
  const formula = data?.score_formula || {};
  const ruleViolationCount = data?.rule_violation_count || 0;

  const severityCounts = data?.severity_breakdown || {};
  const categoryCnts = data?.category_breakdown || {};
  const categories = ['all', ...Object.keys(categoryCnts)];
  const severities = ['all', 'critical', 'high', 'medium', 'low'];

  const filteredResourceDrifts = resourceDrifts.map(rd => {
    const filteredChanges = rd.changes.filter(c => {
      if (filterSeverity !== 'all' && c.severity !== filterSeverity) return false;
      if (filterCategory !== 'all' && c.category !== filterCategory) return false;
      return true;
    });
    return filteredChanges.length > 0 ? { ...rd, changes: filteredChanges } : null;
  }).filter(Boolean);

  const groupedByCategory = {};
  filteredResourceDrifts.forEach(rd => {
    rd.changes.forEach(c => {
      const isRuleViolation = c.category?.startsWith('Rule Violation:');
      const meta = isRuleViolation
        ? { group: 'Baseline Rule Violations', icon: '\u26A0\uFE0F', color: '#7c3aed' }
        : (CATEGORY_META[c.category] || { group: 'Other', icon: '\uD83D\uDCCB', color: '#6b7280' });
      const groupName = meta.group;
      if (!groupedByCategory[groupName]) {
        groupedByCategory[groupName] = { resources: {}, icon: meta.icon, color: meta.color, count: 0 };
      }
      if (!groupedByCategory[groupName].resources[rd.resource_name]) {
        groupedByCategory[groupName].resources[rd.resource_name] = { ...rd, changes: [] };
      }
      groupedByCategory[groupName].resources[rd.resource_name].changes.push(c);
      groupedByCategory[groupName].count++;
    });
  });

  const scoreColor = score > 70 ? '#dc2626' : score > 40 ? '#d97706' : '#059669';
  const scoreLabel = score > 70 ? 'Critical' : score > 40 ? 'Moderate' : 'Minimal';

  const donutData = Object.entries(categoryCnts)
    .filter(([, v]) => v > 0)
    .map(([name, value], i) => {
      const isRule = name.startsWith('Rule Violation:');
      return {
        name: isRule ? 'Rule Violations' : (CATEGORY_META[name]?.group || name),
        value,
        fill: isRule ? '#7c3aed' : (CATEGORY_META[name]?.color || CATEGORY_COLORS[i % CATEGORY_COLORS.length])
      };
    });

  const mergedDonut = [];
  const donutMap = {};
  donutData.forEach(d => {
    if (donutMap[d.name]) donutMap[d.name].value += d.value;
    else { donutMap[d.name] = { ...d }; mergedDonut.push(donutMap[d.name]); }
  });

  const severityDonut = Object.entries(severityCounts)
    .filter(([, v]) => v > 0)
    .map(([name, count]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value: count,
      fill: SEVERITY_COLORS[name]
    }));

  const radialData = [{ name: 'Drift', value: score, fill: scoreColor }];

  const tabStyle = (tab) => ({
    padding: '0.6rem 1.25rem', fontSize: '0.85rem', fontWeight: 600,
    border: 'none', cursor: 'pointer', borderRadius: '8px 8px 0 0',
    background: activeTab === tab ? 'white' : 'transparent',
    color: activeTab === tab ? '#2563eb' : '#6b7280',
    borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
    transition: 'all 0.15s'
  });

  return (
    <div>
      <div className="page-header">
        <h1>Drift Monitoring</h1>
        <p>Configuration drift detection with baseline rules and resource-level change tracking</p>
      </div>

      {baseline && (
        <div style={{
          padding: '1rem 1.25rem', borderRadius: 12, marginBottom: '1.25rem',
          background: 'linear-gradient(135deg, #eff6ff 0%, #f0f9ff 100%)',
          border: '1px solid #bfdbfe'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: '#2563eb',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <FiShield style={{ color: 'white' }} size={20} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e40af' }}>
                Effective Baseline: {baseline.name}
              </div>
              <div style={{ fontSize: '0.78rem', color: '#3b82f6', display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: 2 }}>
                <span>{baseline.resource_count} resources</span>
                <span>{baseline.rule_count || 0} active rules</span>
                <span>Captured {new Date(baseline.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            {ruleViolationCount > 0 && (
              <div style={{
                padding: '0.35rem 0.75rem', borderRadius: 8,
                background: '#fef2f2', border: '1px solid #fecaca',
                fontSize: '0.78rem', fontWeight: 600, color: '#dc2626'
              }}>
                {ruleViolationCount} rule violation{ruleViolationCount > 1 ? 's' : ''}
              </div>
            )}
            <button className="btn btn-secondary" onClick={handleResync} disabled={syncing}
              style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', borderRadius: 8 }}>
              {syncing ? <><FiLoader className="spinning" /> Re-syncing...</> : <><FiActivity size={13} style={{ marginRight: 4 }} /> Re-detect Drift</>}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e5e7eb', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        <button style={tabStyle('compliance')} onClick={() => setActiveTab('compliance')}>
          <FiShield size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Compliance Posture
        </button>
        <button style={tabStyle('policies')} onClick={() => setActiveTab('policies')}>
          <FiLock size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Cloud Policies
          {cloudPolicies && <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#f0fdf4', color: '#166534', padding: '0.1rem 0.4rem', borderRadius: 10 }}>{cloudPolicies.summary?.total_policies || 0}</span>}
        </button>
        <button style={tabStyle('drift')} onClick={() => setActiveTab('drift')}>
          <FiActivity size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Drift Results
        </button>
        <button style={tabStyle('baselines')} onClick={() => setActiveTab('baselines')}>
          <FiDatabase size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Baseline Management
          {baselines.length > 0 && <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#e5e7eb', padding: '0.1rem 0.4rem', borderRadius: 10 }}>{baselines.length}</span>}
        </button>
        <button style={tabStyle('rules')} onClick={() => setActiveTab('rules')}>
          <FiSettings size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
          Baseline Rules
          {baselineRules.length > 0 && <span style={{ marginLeft: 6, fontSize: '0.7rem', background: '#dbeafe', color: '#2563eb', padding: '0.1rem 0.4rem', borderRadius: 10 }}>{baselineRules.length}</span>}
        </button>
      </div>

      {activeTab === 'compliance' && (() => {
        if (complianceLoading) {
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 2rem' }}>
              <FiLoader size={28} style={{ animation: 'spin 1s linear infinite', color: '#2563eb', marginBottom: '1rem' }} />
              <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Fetching compliance data from {
                {azure: 'Azure Security Center', aws: 'AWS Security Hub', gcp: 'GCP Security Command Center'}[cloudProvider] || 'cloud security service'
              }...</p>
              <p style={{ color: '#9ca3af', fontSize: '0.78rem' }}>This may take a moment while we query regulatory compliance standards.</p>
            </div>
          );
        }

        if (complianceError) {
          return (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <FiAlertTriangle size={24} style={{ color: '#d97706', marginBottom: '0.5rem' }} />
              <p style={{ color: '#374151', fontWeight: 600 }}>Could not load compliance data</p>
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>{complianceError}</p>
              <button onClick={() => { setCompliancePosture(null); setComplianceError(null); }} style={{
                marginTop: '1rem', padding: '0.5rem 1rem', background: '#2563eb', color: 'white',
                border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem'
              }}>Retry</button>
            </div>
          );
        }

        const standards = compliancePosture?.standards || {};
        const assessments = compliancePosture?.assessments || [];
        const stdEntries = Object.entries(standards);

        if (stdEntries.length === 0 && assessments.length === 0) {
          const hasDriftData = (data?.resource_drifts || []).length > 0;
          const complianceResults = complianceMemo;
          const allCompliant = Object.values(complianceResults).reduce((s, f) => s + f.compliant, 0);
          const allEvaluated = Object.values(complianceResults).reduce((s, f) => s + f.evaluated, 0);
          const allTotal = Object.values(complianceResults).reduce((s, f) => s + f.total, 0);
          const overallCompliance = allTotal > 0 ? Math.round((allCompliant / allTotal) * 100) : 0;
          const hasAnyEvaluated = allEvaluated > 0;
          const overallColor = !hasAnyEvaluated ? '#9ca3af' : overallCompliance >= 80 ? '#059669' : overallCompliance >= 60 ? '#d97706' : '#dc2626';

          return (
          <>
            <div style={{ marginBottom: '0.75rem', padding: '0.75rem 1rem', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a' }}>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e' }}>
                <FiInfo size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {hasDriftData
                  ? `Showing drift-based compliance estimates. ${
                    {azure: 'Enable Microsoft Defender for Cloud for real Azure regulatory compliance data.',
                     aws: 'Enable AWS Security Hub for real-time compliance findings.',
                     gcp: 'Enable Security Command Center for real-time compliance data.'}[cloudProvider] || 'Enable your cloud security service for real-time data.'}`
                  : `No drift data or ${
                    {azure: 'Azure Security Center', aws: 'AWS Security Hub', gcp: 'GCP Security Command Center'}[cloudProvider] || 'cloud security'
                  } data available. Controls are shown as Not Evaluated. Sync your tenant and enable ${
                    {azure: 'Microsoft Defender for Cloud', aws: 'AWS Security Hub', gcp: 'Security Command Center'}[cloudProvider] || 'your cloud security service'
                  } for compliance assessments.`}
              </p>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#111' }}>Regulatory Compliance Posture</h2>
                <div style={{ padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700, background: !hasAnyEvaluated ? '#f3f4f6' : overallCompliance >= 80 ? '#d1fae5' : overallCompliance >= 60 ? '#fef3c7' : '#fee2e2', color: overallColor }}>
                  {hasAnyEvaluated ? `${overallCompliance}% Overall` : 'Not Assessed'}
                </div>
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>{allTotal} total controls</span>
              </div>
              <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>
                Your current drift and misconfigurations mapped against industry compliance frameworks. Controls marked "At Risk" have active drift findings that may cause non-compliance.
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
              {Object.entries(complianceResults).map(([fwName, fw]) => {
                const fwHasEvaluated = fw.evaluated > 0;
                const pctColor = !fwHasEvaluated ? '#9ca3af' : fw.compliancePercent >= 80 ? '#059669' : fw.compliancePercent >= 60 ? '#d97706' : '#dc2626';
                return (
                  <div key={fwName} className="card" style={{ padding: '1rem 1.25rem', cursor: 'pointer' }} onClick={() => setExpandedFramework(expandedFramework === fwName ? null : fwName)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ fontSize: '1.2rem' }}>{fw.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{fwName}</div>
                          <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{fw.fullName}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: pctColor, lineHeight: 1 }}>{fwHasEvaluated ? `${fw.compliancePercent}%` : 'N/A'}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{fwHasEvaluated ? 'compliant' : 'not assessed'}</div>
                      </div>
                    </div>
                    <div style={{ height: 5, borderRadius: 3, background: '#f3f4f6', overflow: 'hidden', marginBottom: '0.5rem' }}>
                      <div style={{ height: '100%', width: `${fw.compliancePercent}%`, background: pctColor, borderRadius: 3, transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.73rem', color: '#6b7280', flexWrap: 'wrap' }}>
                      {fw.compliant > 0 && <span style={{ color: '#059669', fontWeight: 600 }}><FiCheck size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />{fw.compliant} Passing</span>}
                      {fw.atRisk > 0 && <span style={{ color: '#dc2626', fontWeight: 600 }}><FiAlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />{fw.atRisk} At Risk</span>}
                      {fw.notEvaluated > 0 && <span style={{ color: '#9ca3af' }}>{fw.notEvaluated} Not Evaluated</span>}
                      <span style={{ color: '#9ca3af' }}>{fw.total} controls</span>
                      <span style={{ marginLeft: 'auto', color: '#6b7280' }}>
                        {expandedFramework === fwName ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      </span>
                    </div>
                    {expandedFramework === fwName && (
                      <div style={{ marginTop: '0.75rem', borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem' }}>
                        {fw.controls.map((ctrl, ci) => {
                          const statusColor = ctrl.status === 'compliant' ? '#059669' : ctrl.status === 'at_risk' ? '#dc2626' : '#9ca3af';
                          const statusLabel = ctrl.status === 'compliant' ? 'Passing' : ctrl.status === 'at_risk' ? 'At Risk' : 'Not Evaluated';
                          const statusBg = ctrl.status === 'compliant' ? '#d1fae5' : ctrl.status === 'at_risk' ? '#fee2e2' : '#f3f4f6';
                          return (
                            <div key={ci} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.45rem 0', borderBottom: ci < fw.controls.length - 1 ? '1px solid #f9fafb' : 'none', gap: '0.5rem' }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: '0.78rem', color: '#374151', fontWeight: 500 }}>{ctrl.id}</div>
                                <div style={{ fontSize: '0.73rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ctrl.name}</div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                                <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{ctrl.category}</span>
                                <span style={{ padding: '0.15rem 0.45rem', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600, background: statusBg, color: statusColor }}>{statusLabel}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
          );
        }

        const STANDARD_DISPLAY = {
          'azure-cis-1.4.0': { name: 'CIS Azure', fullName: 'CIS Microsoft Azure Foundations Benchmark', icon: '🛡️', color: '#1e40af' },
          'azure-cis-1.1.0': { name: 'CIS Azure v1.1', fullName: 'CIS Microsoft Azure Foundations Benchmark v1.1', icon: '🛡️', color: '#1e40af' },
          'azure-cis-1.3.0': { name: 'CIS Azure v1.3', fullName: 'CIS Microsoft Azure Foundations Benchmark v1.3', icon: '🛡️', color: '#1e40af' },
          'azure-cis-2.0.0': { name: 'CIS Azure v2.0', fullName: 'CIS Microsoft Azure Foundations Benchmark v2.0', icon: '🛡️', color: '#1e40af' },
          'SOC-TSP': { name: 'SOC 2', fullName: 'SOC 2 Type II (AICPA Trust Service Criteria)', icon: '📋', color: '#7c3aed' },
          'NIST-SP-800-53-R4': { name: 'NIST 800-53 R4', fullName: 'NIST SP 800-53 Rev. 4', icon: '🏛️', color: '#0369a1' },
          'NIST-SP-800-53-R5': { name: 'NIST 800-53 R5', fullName: 'NIST SP 800-53 Rev. 5', icon: '🏛️', color: '#0369a1' },
          'NIST-SP-800-171-R2': { name: 'NIST 800-171', fullName: 'NIST SP 800-171 Rev. 2', icon: '🏛️', color: '#0369a1' },
          'PCI-DSS-3.2.1': { name: 'PCI-DSS', fullName: 'PCI DSS v3.2.1', icon: '💳', color: '#0d9488' },
          'PCI-DSS-4': { name: 'PCI-DSS v4', fullName: 'PCI DSS v4.0', icon: '💳', color: '#0d9488' },
          'ISO-27001-2013': { name: 'ISO 27001', fullName: 'ISO/IEC 27001:2013', icon: '🔒', color: '#4338ca' },
          'HIPAA-HITRUST': { name: 'HIPAA', fullName: 'HIPAA / HITRUST', icon: '🏥', color: '#dc2626' },
          'Azure-Security-Benchmark': { name: 'Azure Security Benchmark', fullName: 'Microsoft Cloud Security Benchmark', icon: '🔐', color: '#2563eb' },
          'Azure-Security-Benchmark-v3': { name: 'Azure Security Benchmark v3', fullName: 'Microsoft Cloud Security Benchmark v3', icon: '🔐', color: '#2563eb' },
          'CMMC-L3': { name: 'CMMC Level 3', fullName: 'Cybersecurity Maturity Model Certification Level 3', icon: '⚔️', color: '#1e3a5f' },
          'FedRAMP-H': { name: 'FedRAMP High', fullName: 'FedRAMP High Impact', icon: '🏢', color: '#1d4ed8' },
          'FedRAMP-M': { name: 'FedRAMP Moderate', fullName: 'FedRAMP Moderate Impact', icon: '🏢', color: '#3b82f6' },
          'SWIFT-CSP-CSCF-v2022': { name: 'SWIFT CSP', fullName: 'SWIFT Customer Security Programme', icon: '🏦', color: '#0f766e' },
          'CIS-Microsoft-Azure-Foundations-Benchmark-v2.0.0': { name: 'CIS Azure v2.0', fullName: 'CIS Microsoft Azure Foundations Benchmark v2.0', icon: '🛡️', color: '#1e40af' },
          'CIS Azure': { name: 'CIS Azure', fullName: 'CIS Microsoft Azure Foundations Benchmark v2.1.0', icon: '🛡️', color: '#1e40af' },
          'HIPAA': { name: 'HIPAA', fullName: 'Health Insurance Portability and Accountability Act', icon: '🏥', color: '#dc2626' },
          'GDPR': { name: 'GDPR', fullName: 'General Data Protection Regulation (EU) 2016/679', icon: '🇪🇺', color: '#2563eb' },
          'SOC 2': { name: 'SOC 2', fullName: 'SOC 2 Type II (AICPA Trust Service Criteria 2017)', icon: '📋', color: '#7c3aed' },
          'NIST 800-53': { name: 'NIST 800-53', fullName: 'NIST SP 800-53 Rev. 5 Security and Privacy Controls', icon: '🏛️', color: '#0369a1' },
          'PCI-DSS': { name: 'PCI-DSS', fullName: 'Payment Card Industry Data Security Standard v4.0.1', icon: '💳', color: '#0d9488' },
          'ISO 27001': { name: 'ISO 27001', fullName: 'ISO/IEC 27001:2022 Annex A Controls', icon: '🔒', color: '#4338ca' },
        };

        const getStdDisplay = (stdName) => {
          if (STANDARD_DISPLAY[stdName]) return STANDARD_DISPLAY[stdName];
          const lower = stdName.toLowerCase();
          for (const [key, val] of Object.entries(STANDARD_DISPLAY)) {
            if (lower.includes(key.toLowerCase())) return val;
          }
          return { name: stdName.replace(/-/g, ' '), fullName: stdName.replace(/-/g, ' '), icon: '📄', color: '#6b7280' };
        };

        const isPolicySource = ['azure_policy', 'aws_config', 'gcp_org_policy'].includes(compliancePosture?.source);

        const allPassed = stdEntries.reduce((s, [, std]) => s + std.passed_controls, 0);
        const allFailed = stdEntries.reduce((s, [, std]) => s + std.failed_controls, 0);
        const allSkipped = stdEntries.reduce((s, [, std]) => s + std.skipped_controls, 0);
        const totalControls = allPassed + allFailed + allSkipped;
        const overallPct = totalControls > 0 ? Math.round((allPassed / totalControls) * 100) : 0;
        const overallColor = overallPct >= 80 ? '#059669' : overallPct >= 60 ? '#d97706' : '#dc2626';

        const unhealthyAssessments = assessments.filter(a => a.status === 'Unhealthy');

        return (
        <>
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.15rem', color: '#111' }}>Regulatory Compliance Posture</h2>
                <div style={{
                  padding: '0.25rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 700,
                  background: overallPct >= 80 ? '#d1fae5' : overallPct >= 60 ? '#fef3c7' : '#fee2e2',
                  color: overallColor
                }}>
                  {overallPct}% Overall
                </div>
                <div style={{ padding: '0.2rem 0.55rem', borderRadius: 6, fontSize: '0.7rem', fontWeight: 600, background: isPolicySource ? '#fef3c7' : '#dbeafe', color: isPolicySource ? '#92400e' : '#2563eb' }}>
                  {isPolicySource
                    ? `Derived from ${({azure: 'Azure Policy', aws: 'AWS Config Rules', gcp: 'Organization Policies'})[cloudProvider] || 'cloud policy'}`
                    : `Live from ${({azure: 'Azure Security Center', aws: 'AWS Security Hub', gcp: 'GCP Security Command Center'})[cloudProvider] || 'cloud security'}`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.25rem', background: '#f3f4f6', borderRadius: 8, padding: '0.2rem' }}>
                <button onClick={() => setComplianceViewMode('all')} style={{
                  padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: complianceViewMode === 'all' ? 'white' : 'transparent',
                  color: complianceViewMode === 'all' ? '#2563eb' : '#6b7280',
                  boxShadow: complianceViewMode === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}>All Controls</button>
                <button onClick={() => setComplianceViewMode('at_risk')} style={{
                  padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: complianceViewMode === 'at_risk' ? 'white' : 'transparent',
                  color: complianceViewMode === 'at_risk' ? '#dc2626' : '#6b7280',
                  boxShadow: complianceViewMode === 'at_risk' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}>
                  <FiAlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                  Failed ({allFailed})
                </button>
                <button onClick={() => setComplianceViewMode('passing')} style={{
                  padding: '0.35rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, border: 'none', borderRadius: 6, cursor: 'pointer',
                  background: complianceViewMode === 'passing' ? 'white' : 'transparent',
                  color: complianceViewMode === 'passing' ? '#059669' : '#6b7280',
                  boxShadow: complianceViewMode === 'passing' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}>
                  <FiCheck size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                  Passed ({allPassed})
                </button>
              </div>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#6b7280', margin: 0 }}>
              {isPolicySource
                ? `Compliance results derived from ${({azure: 'Azure Policy', aws: 'AWS Config', gcp: 'Organization Policy'})[cloudProvider] || 'cloud policy'} evaluations. ${({azure: 'Enable Microsoft Defender for Cloud for full regulatory compliance data.', aws: 'Enable AWS Security Hub for full compliance findings.', gcp: 'Enable Security Command Center for full compliance data.'})[cloudProvider] || 'Enable your cloud security service for full compliance data.'}`
                : `Real-time compliance posture from ${({azure: 'Azure Security Center regulatory compliance assessments', aws: 'AWS Security Hub compliance standards', gcp: 'GCP Security Command Center findings'})[cloudProvider] || 'cloud security assessments'}. Data reflects your actual ${({azure: 'Azure subscription', aws: 'AWS account', gcp: 'GCP project'})[cloudProvider] || 'cloud'} configuration.`}
            </p>
          </div>

          {unhealthyAssessments.length > 0 && (
            <div style={{ marginBottom: '1.5rem', padding: '0.85rem 1rem', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#991b1b', marginBottom: '0.35rem' }}>
                <FiAlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {unhealthyAssessments.length} Security Assessment{unhealthyAssessments.length !== 1 ? 's' : ''} Require Attention
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                {unhealthyAssessments.slice(0, 8).map((a, i) => (
                  <span key={i} style={{ padding: '0.2rem 0.55rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 500, background: '#fee2e2', color: '#b91c1c' }}>
                    {a.name}
                  </span>
                ))}
                {unhealthyAssessments.length > 8 && (
                  <span style={{ padding: '0.2rem 0.55rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 500, background: '#fee2e2', color: '#b91c1c' }}>
                    +{unhealthyAssessments.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {stdEntries.map(([stdName, std]) => {
              const display = getStdDisplay(stdName);
              const totalCtrl = std.passed_controls + std.failed_controls + (std.skipped_controls || 0);
              const pct = totalCtrl > 0 ? Math.round((std.passed_controls / totalCtrl) * 100) : 0;
              const pctColor = pct >= 80 ? '#059669' : pct >= 60 ? '#d97706' : '#dc2626';
              const isExpanded = expandedFramework === stdName;
              const controls = std.controls || [];

              return (
                <div key={stdName} className="card" style={{
                  padding: 0, overflow: 'hidden', cursor: 'pointer',
                  border: isExpanded ? `2px solid ${display.color}` : '1px solid #e5e7eb',
                  gridColumn: isExpanded ? '1 / -1' : undefined,
                  transition: 'all 0.2s'
                }}>
                  <div onClick={() => setExpandedFramework(isExpanded ? null : stdName)}
                    style={{
                      padding: '1rem 1.25rem',
                      background: isExpanded ? `${display.color}08` : 'white',
                      borderBottom: isExpanded ? `1px solid ${display.color}22` : 'none'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ fontSize: '1.3rem' }}>{display.icon}</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#111' }}>{display.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{display.fullName}</div>
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: pctColor, lineHeight: 1 }}>{pct}%</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>compliant</div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.6rem' }}>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: '#f3f4f6', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, width: `${pct}%`, background: pctColor, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', flexWrap: 'wrap' }}>
                      {std.passed_controls > 0 && (
                        <span style={{ color: '#059669', fontWeight: 600 }}>
                          <FiCheck size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {std.passed_controls} Passed
                        </span>
                      )}
                      {std.failed_controls > 0 && (
                        <span style={{ color: '#dc2626', fontWeight: 600 }}>
                          <FiAlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                          {std.failed_controls} Failed
                        </span>
                      )}
                      {std.skipped_controls > 0 && (
                        <span style={{ color: '#9ca3af', fontWeight: 500 }}>{std.skipped_controls} Skipped</span>
                      )}
                      <span style={{ color: '#9ca3af' }}>{controls.length || totalCtrl} controls</span>
                      <span style={{ marginLeft: 'auto', color: '#6b7280' }}>
                        {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      </span>
                    </div>
                  </div>

                  {isExpanded && controls.length > 0 && (() => {
                    const filtered = controls.filter(ctrl => {
                      if (complianceViewMode === 'at_risk') return ctrl.state === 'Failed';
                      if (complianceViewMode === 'passing') return ctrl.state === 'Passed';
                      return true;
                    });
                    return (
                    <div style={{ padding: '0.75rem 1.25rem 1.25rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <p style={{ fontSize: '0.78rem', color: '#6b7280', margin: 0 }}>{display.fullName}</p>
                        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9ca3af', whiteSpace: 'nowrap', marginLeft: '1rem' }}>
                          Showing {filtered.length} of {controls.length} controls
                        </span>
                      </div>

                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', tableLayout: 'fixed' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                            <th style={{ width: '30px', padding: '0.5rem 0.25rem' }}></th>
                            <th style={{ width: '90px', textAlign: 'left', padding: '0.5rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                            <th style={{ width: '110px', textAlign: 'left', padding: '0.5rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Control ID</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Description</th>
                            <th style={{ width: '70px', textAlign: 'right', padding: '0.5rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Passed</th>
                            <th style={{ width: '70px', textAlign: 'right', padding: '0.5rem 0.5rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Failed</th>
                            <th style={{ width: '75px', textAlign: 'center', padding: '0.5rem 0.25rem', fontSize: '0.72rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...filtered].sort((a, b) => {
                            const order = { Failed: 0, Passed: 1, Skipped: 2, Unsupported: 3 };
                            return (order[a.state] ?? 3) - (order[b.state] ?? 3);
                          }).map(ctrl => {
                            const ctrlKey = `${stdName}::${ctrl.id}`;
                            const isCtrlExpanded = expandedControl === ctrlKey;
                            const ctrlResources = controlResources[ctrlKey] || [];
                            const ctrlResLoading = controlResourcesLoading[ctrlKey];
                            return (
                            <React.Fragment key={ctrl.id}>
                            <tr style={{
                              borderBottom: isCtrlExpanded ? 'none' : '1px solid #f3f4f6',
                              background: ctrl.state === 'Failed' ? '#fef2f2' : 'transparent',
                              cursor: ctrl.failed_assessments > 0 ? 'pointer' : 'default',
                            }} onClick={() => ctrl.failed_assessments > 0 && handleExpandControl(stdName, ctrl.id)}>
                              <td style={{ padding: '0.55rem 0.25rem', textAlign: 'center' }}>
                                {ctrl.failed_assessments > 0 && (
                                  isCtrlExpanded ? <FiChevronDown size={13} color="#6b7280" /> : <FiChevronRight size={13} color="#6b7280" />
                                )}
                              </td>
                              <td style={{ padding: '0.55rem 0.5rem' }}>
                                {ctrl.state === 'Failed' ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.15rem 0.5rem', borderRadius: 4, background: '#fee2e2', color: '#dc2626', fontSize: '0.72rem', fontWeight: 700 }}>
                                    <FiAlertTriangle size={11} /> Failed
                                  </span>
                                ) : ctrl.state === 'Passed' ? (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.15rem 0.5rem', borderRadius: 4, background: '#d1fae5', color: '#059669', fontSize: '0.72rem', fontWeight: 700 }}>
                                    <FiCheck size={11} /> Passed
                                  </span>
                                ) : (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.15rem 0.5rem', borderRadius: 4, background: '#f3f4f6', color: '#9ca3af', fontSize: '0.72rem', fontWeight: 700 }}>
                                    <FiInfo size={11} /> {ctrl.state}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '0.55rem 0.5rem', fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>{ctrl.id}</td>
                              <td style={{ padding: '0.55rem 0.5rem', color: '#374151', fontSize: '0.8rem', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{ctrl.description || ctrl.id}</td>
                              <td style={{ padding: '0.55rem 0.5rem', textAlign: 'right' }}>
                                {ctrl.passed_assessments > 0 ? (
                                  <span style={{ color: '#059669', fontWeight: 700 }}>{ctrl.passed_assessments}</span>
                                ) : <span style={{ color: '#9ca3af' }}>0</span>}
                              </td>
                              <td style={{ padding: '0.55rem 0.5rem', textAlign: 'right' }}>
                                {ctrl.failed_assessments > 0 ? (
                                  <span style={{ color: '#dc2626', fontWeight: 700 }}>{ctrl.failed_assessments}</span>
                                ) : <span style={{ color: '#9ca3af' }}>0</span>}
                              </td>
                              <td style={{ padding: '0.55rem 0.25rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                <button
                                  onClick={() => handleAskAI('compliance control', ctrl.description || ctrl.id, ctrl.description, display.name, ctrl.state, ctrl.failed_assessments > 0 ? 'high' : 'low', ctrlResources)}
                                  disabled={aiLoading === `compliance control::${ctrl.description || ctrl.id}`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    padding: '0.2rem 0.45rem', borderRadius: 5, border: '1px solid #c7d2fe',
                                    background: aiLoading === `compliance control::${ctrl.description || ctrl.id}` ? '#e0e7ff' : '#eef2ff',
                                    color: '#4f46e5', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer',
                                    opacity: aiLoading === `compliance control::${ctrl.description || ctrl.id}` ? 0.7 : 1,
                                  }}
                                  title="Get AI remediation guidance"
                                >
                                  {aiLoading === `compliance control::${ctrl.description || ctrl.id}` ? <FiLoader size={10} className="spin" /> : <span style={{ fontSize: '0.7rem' }}>&#10024;</span>}
                                  Ask AI
                                </button>
                              </td>
                            </tr>
                            {isCtrlExpanded && (
                              <tr>
                                <td colSpan={7} style={{ padding: 0, background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                                  <div style={{ padding: '0.75rem 1rem 0.75rem 2.5rem' }}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#374151', marginBottom: '0.5rem' }}>
                                      <FiAlertTriangle size={12} style={{ verticalAlign: 'middle', marginRight: 4, color: '#dc2626' }} />
                                      Affected Resources / Assessments
                                    </div>
                                    {ctrlResLoading ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 0', fontSize: '0.78rem', color: '#6b7280' }}>
                                        <FiLoader size={12} className="spin" /> Loading affected resources...
                                      </div>
                                    ) : ctrlResources.length === 0 ? (
                                      <div style={{ fontSize: '0.78rem', color: '#9ca3af', padding: '0.5rem 0' }}>No detailed resource data available for this control.</div>
                                    ) : (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                        {ctrlResources.map((res, ri) => (
                                          <div key={ri} style={{
                                            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem',
                                            background: res.state === 'Failed' ? '#fef2f2' : '#f0fdf4', borderRadius: 6,
                                            border: `1px solid ${res.state === 'Failed' ? '#fecaca' : '#bbf7d0'}`,
                                          }}>
                                            {res.state === 'Failed' ? (
                                              <FiAlertTriangle size={11} color="#dc2626" />
                                            ) : (
                                              <FiCheck size={11} color="#059669" />
                                            )}
                                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151', flex: 1, wordBreak: 'break-all' }}>
                                              {res.resource_name || res.assessment_id}
                                            </span>
                                            <span style={{
                                              fontSize: '0.65rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 4,
                                              background: res.state === 'Failed' ? '#fee2e2' : '#dcfce7',
                                              color: res.state === 'Failed' ? '#dc2626' : '#059669',
                                            }}>{res.state}</span>
                                            {res.description && (
                                              <span style={{ fontSize: '0.7rem', color: '#6b7280', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={res.description}>
                                                {res.description}
                                              </span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </>
        );
      })()}

      {activeTab === 'drift' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
            <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '1.5rem 1rem' }}>
              <p style={{ fontSize: '0.7rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem' }}>Drift Score</p>
              <ResponsiveContainer width="100%" height={140}>
                <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={radialData} barSize={14}>
                  <RadialBar background={{ fill: '#f3f4f6' }} dataKey="value" cornerRadius={10} />
                </RadialBarChart>
              </ResponsiveContainer>
              <div style={{ marginTop: -40, textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}</div>
                <div style={{ fontSize: '0.75rem', color: scoreColor, fontWeight: 600, marginTop: 4 }}>{scoreLabel} Drift</div>
              </div>
              {trend.direction && (
                <div style={{
                  marginTop: '0.75rem', fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: 20,
                  background: trend.direction === 'improving' ? '#d1fae5' : trend.direction === 'degrading' ? '#fee2e2' : '#f3f4f6',
                  color: trend.direction === 'improving' ? '#065f46' : trend.direction === 'degrading' ? '#991b1b' : '#6b7280'
                }}>
                  {trend.direction === 'improving' ? '\u2193' : trend.direction === 'degrading' ? '\u2191' : '\u2192'} {trend.direction} ({trend.change_pct > 0 ? '+' : ''}{trend.change_pct}%)
                </div>
              )}
              <div style={{ display: 'flex', gap: '1.5rem', marginTop: '0.75rem', fontSize: '0.75rem', color: '#6b7280' }}>
                <span><strong style={{ color: '#111', fontSize: '0.9rem' }}>{data?.total_findings || 0}</strong> changes</span>
                <span><strong style={{ color: '#111', fontSize: '0.9rem' }}>{data?.affected_resources || 0}</strong> resources</span>
              </div>
              <button onClick={() => setShowFormula(!showFormula)}
                style={{
                  marginTop: '0.75rem', fontSize: '0.7rem', color: '#6366f1', background: 'none',
                  border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
                  textDecoration: 'underline', textUnderlineOffset: 2
                }}>
                <FiInfo size={11} /> How is this calculated?
              </button>
            </div>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '0.75rem' }}>Severity Distribution</h3>
              {severityDonut.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={severityDonut} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={50} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                      {severityDonut.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.8rem' }}
                      formatter={(value, name) => [`${value} findings`, name]} />
                    <Legend iconType="circle" iconSize={8}
                      formatter={(val) => <span style={{ fontSize: '0.75rem', color: '#374151' }}>{val}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p style={{ color: '#9ca3af', textAlign: 'center', padding: '3rem 0' }}>No data</p>}
            </div>

            <div className="card" style={{ padding: '1.25rem' }}>
              <h3 style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '0.75rem' }}>Drift by Domain</h3>
              {mergedDonut.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={mergedDonut} dataKey="value" nameKey="name" cx="50%" cy="50%"
                      innerRadius={50} outerRadius={80} paddingAngle={3} strokeWidth={0}>
                      {mergedDonut.map((entry, idx) => <Cell key={idx} fill={entry.fill} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.8rem' }}
                      formatter={(value, name) => [`${value} findings`, name]} />
                    <Legend iconType="circle" iconSize={8}
                      formatter={(val) => <span style={{ fontSize: '0.75rem', color: '#374151' }}>{val}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p style={{ color: '#9ca3af', textAlign: 'center', padding: '3rem 0' }}>No data</p>}
            </div>
          </div>

          {showFormula && (
            <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)', border: '1px solid #ddd6fe' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#5b21b6', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiZap size={16} /> Drift Score Calculation
                </h3>
                <button onClick={() => setShowFormula(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={16} /></button>
              </div>
              <div style={{ background: 'white', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem', border: '1px solid #e9d5ff' }}>
                <div style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#5b21b6', textAlign: 'center', fontWeight: 600 }}>
                  Score = ( Weighted Total / Max Possible ) x 100
                </div>
                <div style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: '#7c3aed', textAlign: 'center', marginTop: 8 }}>
                  Score = ( {formula.weighted_total || 0} / {formula.max_possible || 1} ) x 100 = <strong style={{ fontSize: '1.1rem' }}>{score}</strong>
                </div>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#4c1d95', marginBottom: '0.75rem', fontWeight: 600 }}>Severity Weights & Contributions</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.75rem' }}>
                {Object.entries(formula.severity_contributions || {}).map(([sev, info]) => (
                  <div key={sev} style={{
                    background: 'white', borderRadius: 10, padding: '0.75rem 1rem',
                    border: `2px solid ${SEVERITY_COLORS[sev] || '#e5e7eb'}`, textAlign: 'center'
                  }}>
                    <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', fontWeight: 700, color: SEVERITY_COLORS[sev], letterSpacing: '0.05em' }}>{sev}</div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#111', margin: '0.25rem 0' }}>
                      {info.count} <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400 }}>x {info.weight}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>= {info.subtotal} pts</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#6b7280', lineHeight: 1.5 }}>
                Each finding is weighted by severity: Critical (x8), High (x5), Medium (x3), Low (x1).
                Rule violations from your baseline rules are included in this calculation.
              </div>
            </div>
          )}

          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem' }}>
                <FiLayers size={18} /> Drifted Resources
                <span style={{ background: '#f3f4f6', color: '#374151', fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 20, fontWeight: 600 }}>
                  {filteredResourceDrifts.length}
                </span>
              </h3>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
                  style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.8rem', background: 'white' }}>
                  {severities.map(s => <option key={s} value={s}>{s === 'all' ? 'All Severities' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
                <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
                  style={{ padding: '0.4rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.8rem', background: 'white' }}>
                  {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c}</option>)}
                </select>
              </div>
            </div>

            {Object.keys(groupedByCategory).length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {Object.entries(groupedByCategory).map(([groupName, group]) => {
                  const isGroupExpanded = expandedGroups[groupName] !== false;
                  const resourceList = Object.values(group.resources);
                  const groupSevCounts = { critical: 0, high: 0, medium: 0, low: 0 };
                  resourceList.forEach(rd => rd.changes.forEach(c => { groupSevCounts[c.severity] = (groupSevCounts[c.severity] || 0) + 1; }));
                  return (
                    <div key={groupName} style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                      <div onClick={() => setExpandedGroups(prev => ({ ...prev, [groupName]: !isGroupExpanded }))}
                        style={{
                          padding: '0.75rem 1rem', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: groupName === 'Baseline Rule Violations'
                            ? 'linear-gradient(135deg, #f5f3ff 0%, #faf5ff 100%)'
                            : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                          borderBottom: isGroupExpanded ? '1px solid #e5e7eb' : 'none'
                        }}>
                        {isGroupExpanded ? <FiChevronDown size={16} color="#6b7280" /> : <FiChevronRight size={16} color="#6b7280" />}
                        <span style={{ fontSize: '1.1rem' }}>{group.icon}</span>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111' }}>{groupName}</span>
                          <span style={{ color: '#6b7280', fontSize: '0.8rem', marginLeft: 8 }}>
                            {resourceList.length} resource{resourceList.length > 1 ? 's' : ''} affected
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                          {Object.entries(groupSevCounts).filter(([,v]) => v > 0).map(([sev, cnt]) => (
                            <span key={sev} style={{
                              padding: '0.1rem 0.4rem', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600,
                              background: SEVERITY_BG[sev], color: SEVERITY_COLORS[sev]
                            }}>{cnt} {sev}</span>
                          ))}
                        </div>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: group.color, flexShrink: 0 }} />
                      </div>
                      {isGroupExpanded && (
                        <div>
                          {resourceList.map((rd, i) => {
                            const isExpanded = expandedResources[`${groupName}-${rd.resource_name}`];
                            const maxSev = rd.changes.reduce((max, c) => {
                              const order = { critical: 4, high: 3, medium: 2, low: 1 };
                              return (order[c.severity] || 0) > (order[max] || 0) ? c.severity : max;
                            }, 'low');
                            const ruleViolations = rd.changes.filter(c => c.rule_id);
                            const configChanges = rd.changes.filter(c => !c.rule_id);
                            const shortType = (rd.resource_type || '').split('/').pop() || 'Resource';
                            return (
                              <div key={i} style={{ borderBottom: i < resourceList.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                                <div onClick={() => setExpandedResources(prev => ({
                                  ...prev, [`${groupName}-${rd.resource_name}`]: !prev[`${groupName}-${rd.resource_name}`]
                                }))}
                                  style={{
                                    padding: '0.75rem 1rem 0.75rem 2.5rem', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '0.65rem',
                                    background: isExpanded ? '#fafafa' : 'white',
                                    borderLeft: `3px solid ${SEVERITY_COLORS[maxSev]}`
                                  }}>
                                  {isExpanded ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                      {rd.resource_name}
                                      <span style={{
                                        fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: 4,
                                        background: '#f3f4f6', color: '#6b7280', fontWeight: 500
                                      }}>{shortType}</span>
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2, display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                      <span>RG: {rd.resource_group}</span>
                                      <span>Region: {rd.location}</span>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                    {ruleViolations.length > 0 && (
                                      <span style={{
                                        padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600,
                                        background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe'
                                      }}>{ruleViolations.length} rule violation{ruleViolations.length > 1 ? 's' : ''}</span>
                                    )}
                                    {configChanges.length > 0 && (
                                      <span style={{
                                        padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600,
                                        background: '#fff7ed', color: '#ea580c', border: '1px solid #fed7aa'
                                      }}>{configChanges.length} config change{configChanges.length > 1 ? 's' : ''}</span>
                                    )}
                                    <span style={{
                                      padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600,
                                      background: SEVERITY_BG[maxSev], color: SEVERITY_COLORS[maxSev], textTransform: 'capitalize'
                                    }}>{maxSev}</span>
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div style={{ background: '#fafafa', borderTop: '1px solid #f3f4f6' }}>
                                    {rd.changes.map((c, j) => {
                                      const isRuleViolation = !!c.rule_id;
                                      const changeLabel = c.change_type === 'added' ? 'Added' : c.change_type === 'tag_removed' ? 'Removed' : 'Modified';
                                      const changeLabelColor = c.change_type === 'added' ? '#059669' : c.change_type === 'tag_removed' ? '#d97706' : '#dc2626';
                                      const changeExplanation = getChangeExplanation(c);
                                      const impact = getImpactDescription(c);
                                      const action = getRecommendedAction(c);
                                      return (
                                        <div key={j} style={{
                                          padding: '1rem 1.25rem 1rem 3.5rem',
                                          borderBottom: j < rd.changes.length - 1 ? '1px solid #e5e7eb' : 'none'
                                        }}>
                                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.6rem' }}>
                                            <div style={{
                                              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                                              background: isRuleViolation ? '#f5f3ff' : (c.change_type === 'added' ? '#d1fae5' : '#fef2f2'),
                                              border: `1px solid ${isRuleViolation ? '#ddd6fe' : (c.change_type === 'added' ? '#a7f3d0' : '#fecaca')}`,
                                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                                              fontSize: '0.85rem', fontWeight: 700
                                            }}>
                                              {isRuleViolation ? <FiAlertTriangle size={14} color="#7c3aed" /> : (
                                                <span style={{ color: changeLabelColor }}>{CHANGE_ICONS[c.change_type] || '~'}</span>
                                              )}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111', marginBottom: 3, lineHeight: 1.4 }}>
                                                {c.description}
                                              </div>
                                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.72rem' }}>
                                                <span style={{
                                                  padding: '0.1rem 0.4rem', borderRadius: 4, fontWeight: 600,
                                                  background: isRuleViolation ? '#f5f3ff' : '#f3f4f6',
                                                  color: isRuleViolation ? '#7c3aed' : changeLabelColor
                                                }}>{isRuleViolation ? 'Rule Violation' : changeLabel}</span>
                                                <span style={{ color: '#9ca3af' }}>{c.detected_by}</span>
                                                {c.detected_at && (
                                                  <span style={{ color: '#b0b0b0' }}>
                                                    <FiClock style={{ marginRight: 2, verticalAlign: 'middle' }} size={10} />
                                                    {new Date(c.detected_at).toLocaleString()}
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                            <span style={{
                                              padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600,
                                              background: SEVERITY_BG[c.severity], color: SEVERITY_COLORS[c.severity], textTransform: 'capitalize'
                                            }}>{c.severity}</span>
                                          </div>

                                          {isRuleViolation && c.rule_name && (
                                            <div style={{
                                              marginLeft: 36, marginBottom: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 8,
                                              background: '#f5f3ff', border: '1px solid #e9d5ff'
                                            }}>
                                              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', marginBottom: 4 }}>
                                                Violated Rule
                                              </div>
                                              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#5b21b6', marginBottom: 2 }}>{c.rule_name}</div>
                                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                <span style={{ color: '#374151', fontWeight: 500 }}>Property:</span> {c.property_changed}
                                              </div>
                                            </div>
                                          )}

                                          {(c.baseline_value || c.current_value) && (
                                            <div style={{
                                              marginLeft: 36, marginBottom: '0.5rem', padding: '0.6rem 0.75rem', borderRadius: 8,
                                              background: 'white', border: '1px solid #e5e7eb'
                                            }}>
                                              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', marginBottom: 6 }}>
                                                What Changed
                                              </div>
                                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                {changeExplanation && (
                                                  <div style={{
                                                    fontSize: '0.8rem', color: '#374151', lineHeight: 1.5,
                                                    padding: '0.5rem 0.6rem', borderRadius: 6,
                                                    background: '#f8fafc', border: '1px solid #e2e8f0'
                                                  }}>
                                                    {changeExplanation}
                                                  </div>
                                                )}
                                                <div style={{
                                                  display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '0.5rem', alignItems: 'stretch',
                                                  padding: '0.4rem 0', borderTop: changeExplanation ? '1px solid #f1f5f9' : 'none',
                                                  paddingTop: changeExplanation ? '0.5rem' : '0'
                                                }}>
                                                  <div style={{
                                                    padding: '0.5rem 0.6rem', borderRadius: 6,
                                                    background: '#f0fdf4', border: '1px solid #bbf7d0'
                                                  }}>
                                                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 3 }}>
                                                      {isRuleViolation ? 'Expected / Required' : 'Baseline / Approved State'}
                                                    </div>
                                                    {c.property_changed && (
                                                      <div style={{ fontSize: '0.68rem', color: '#6b7280', marginBottom: 3 }}>
                                                        <span style={{ fontFamily: 'monospace', background: '#dcfce7', padding: '0.05rem 0.25rem', borderRadius: 2, fontSize: '0.65rem' }}>
                                                          {c.property_changed}
                                                        </span>
                                                      </div>
                                                    )}
                                                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#15803d', wordBreak: 'break-word' }}>
                                                      {c.baseline_value || 'N/A'}
                                                    </div>
                                                  </div>
                                                  <div style={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem', color: '#d1d5db' }}>{'\u2192'}</div>
                                                  <div style={{
                                                    padding: '0.5rem 0.6rem', borderRadius: 6,
                                                    background: '#fef2f2', border: '1px solid #fecaca'
                                                  }}>
                                                    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 3 }}>
                                                      {isRuleViolation ? 'Actual / Current State' : 'Current / Drifted State'}
                                                    </div>
                                                    {c.property_changed && (
                                                      <div style={{ fontSize: '0.68rem', color: '#6b7280', marginBottom: 3 }}>
                                                        <span style={{ fontFamily: 'monospace', background: '#fee2e2', padding: '0.05rem 0.25rem', borderRadius: 2, fontSize: '0.65rem' }}>
                                                          {c.property_changed}
                                                        </span>
                                                      </div>
                                                    )}
                                                    <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#dc2626', wordBreak: 'break-word' }}>
                                                      {c.current_value || 'N/A'}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}

                                          {impact && (
                                            <div style={{
                                              marginLeft: 36, marginBottom: '0.5rem', padding: '0.5rem 0.75rem', borderRadius: 8,
                                              background: '#fffbeb', border: '1px solid #fde68a'
                                            }}>
                                              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#92400e', textTransform: 'uppercase', marginBottom: 3 }}>
                                                Impact
                                              </div>
                                              <div style={{ fontSize: '0.78rem', color: '#78350f', lineHeight: 1.4 }}>{impact}</div>
                                            </div>
                                          )}

                                          {action && (
                                            <div style={{
                                              marginLeft: 36, padding: '0.5rem 0.75rem', borderRadius: 8,
                                              background: '#f0fdf4', border: '1px solid #bbf7d0'
                                            }}>
                                              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#166534', textTransform: 'uppercase', marginBottom: 3 }}>
                                                Recommended Action
                                              </div>
                                              <div style={{ fontSize: '0.78rem', color: '#14532d', lineHeight: 1.4 }}>{action}</div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
                <FiCheck size={32} style={{ marginBottom: '0.75rem', color: '#10b981' }} />
                <p style={{ fontWeight: 500 }}>
                  {data?.total_findings > 0 ? 'No drifts match the current filters.' : 'No drift detected. All resources match the baseline.'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'baselines' && (
        <div>
          <div className="card" style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiDatabase size={18} /> Baseline Management
                </h3>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: 4, marginBottom: 0 }}>
                  Baselines capture resource snapshots and enforce governance rules. Drift is measured by comparing current state against the active baseline's snapshot and rules.
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => { setShowBaselineForm(!showBaselineForm); setBaselineMsg(null); }}
                style={{ padding: '0.45rem 0.85rem', fontSize: '0.8rem', borderRadius: 8, whiteSpace: 'nowrap' }}>
                <FiPlus style={{ marginRight: 4 }} size={14} /> New Baseline
              </button>
            </div>

            {showBaselineForm && (
              <div style={{
                padding: '1.25rem', border: '1px solid #e5e7eb', borderRadius: 10,
                marginBottom: '1.25rem', background: '#f9fafb'
              }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Create a New Baseline Snapshot
                </div>
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>
                  This captures all synced resources as the known-good state. After creating, add governance rules
                  (like VM size limits, required tags, etc.) to enforce policies. Then re-sync to detect drift.
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374141' }}>Baseline Name *</label>
                    <input value={baselineName} onChange={e => setBaselineName(e.target.value)}
                      placeholder="e.g., Q1 2026 Hardening Release"
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.3rem', color: '#374151' }}>Description</label>
                    <input value={baselineDesc} onChange={e => setBaselineDesc(e.target.value)}
                      placeholder="What does this baseline represent?"
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="btn btn-primary" onClick={handleSetBaseline}
                      disabled={settingBaseline || !baselineName.trim()} style={{ padding: '0.5rem 1rem', borderRadius: 8 }}>
                      {settingBaseline ? <><FiLoader className="spinning" /> Saving...</> : 'Capture Snapshot'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => { setShowBaselineForm(false); setBaselineMsg(null); }}
                      style={{ padding: '0.5rem 0.75rem', borderRadius: 8 }}>Cancel</button>
                  </div>
                </div>
                {baselineMsg && (
                  <div style={{
                    marginTop: '0.75rem', padding: '0.6rem 0.85rem', borderRadius: 8,
                    background: baselineMsg.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: baselineMsg.type === 'success' ? '#065f46' : '#991b1b',
                    fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 6
                  }}>
                    {baselineMsg.type === 'success' ? <FiCheck size={14} /> : <FiAlertTriangle size={14} />}
                    {baselineMsg.text}
                  </div>
                )}
              </div>
            )}

            {baselines.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {baselines.map((b, idx) => (
                  <div key={b.id} style={{
                    display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem',
                    border: `1px solid ${b.is_active ? '#bfdbfe' : '#e5e7eb'}`,
                    borderRadius: 10, background: b.is_active ? '#f0f9ff' : 'white', flexWrap: 'wrap',
                    cursor: 'pointer'
                  }} onClick={() => handleSelectBaseline(b)}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: b.is_active ? '#2563eb' : '#e5e7eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <FiShield size={16} style={{ color: b.is_active ? 'white' : '#9ca3af' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {b.name}
                        <span style={{
                          fontSize: '0.65rem', padding: '0.1rem 0.4rem', borderRadius: 4,
                          background: '#e5e7eb', color: '#6b7280', fontWeight: 600
                        }}>v{idx + 1}.0</span>
                        {b.is_active && (
                          <span style={{
                            fontSize: '0.65rem', padding: '0.1rem 0.45rem',
                            borderRadius: 4, background: '#059669', color: 'white', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.05em'
                          }}>Active</span>
                        )}
                      </div>
                      {b.description && <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: 2 }}>{b.description}</div>}
                      <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: 3, display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                        <span>{b.resource_count} resources</span>
                        <span>{b.rule_count || 0} rules ({b.enabled_rule_count || 0} enabled)</span>
                        <span>Created {new Date(b.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                      {!b.is_active && (
                        <button className="btn btn-secondary" onClick={() => handleActivateBaseline(b.id)}
                          style={{ padding: '0.3rem 0.65rem', fontSize: '0.75rem', borderRadius: 6 }}>Activate</button>
                      )}
                      {deleteConfirm === b.id ? (
                        <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                          <span style={{ fontSize: '0.7rem', color: '#dc2626' }}>Delete?</span>
                          <button onClick={() => handleDeleteBaseline(b.id)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', borderRadius: 4, background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer' }}>Yes</button>
                          <button onClick={() => setDeleteConfirm(null)}
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem', borderRadius: 4, background: '#e5e7eb', color: '#374151', border: 'none', cursor: 'pointer' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(b.id)}
                          style={{ padding: '0.3rem', borderRadius: 6, background: 'none', border: '1px solid #e5e7eb', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                textAlign: 'center', padding: '2.5rem', color: '#9ca3af',
                background: '#f9fafb', borderRadius: 10, border: '1px dashed #d1d5db'
              }}>
                <FiDatabase size={28} style={{ marginBottom: '0.75rem', color: '#d1d5db' }} />
                <p style={{ fontWeight: 500, color: '#6b7280', marginBottom: '0.25rem' }}>No baselines created yet</p>
                <p style={{ fontSize: '0.75rem' }}>Create a baseline to capture resource state and add governance rules.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'rules' && (
        <div>
          {!selectedBaseline ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <FiSettings size={32} style={{ color: '#d1d5db', marginBottom: '0.75rem' }} />
              <p style={{ fontWeight: 500, color: '#6b7280' }}>No baseline selected</p>
              <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Create or select a baseline from the Baseline Management tab to manage its rules.</p>
            </div>
          ) : (
            <>
              <div style={{
                padding: '0.75rem 1rem', borderRadius: 10, marginBottom: '1rem',
                background: selectedBaseline.is_active ? '#f0f9ff' : '#f9fafb',
                border: `1px solid ${selectedBaseline.is_active ? '#bfdbfe' : '#e5e7eb'}`,
                display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap'
              }}>
                <FiShield size={16} color={selectedBaseline.is_active ? '#2563eb' : '#9ca3af'} />
                <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                  Rules for: {selectedBaseline.name}
                  {selectedBaseline.is_active && <span style={{ marginLeft: 6, fontSize: '0.65rem', color: '#059669' }}>(Active)</span>}
                </span>
                <div style={{ flex: 1 }} />
                <button className="btn btn-primary" onClick={() => { setShowTemplates(true); setShowCustomRule(false); }}
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', borderRadius: 8, marginRight: 4 }}>
                  <FiPlus size={12} style={{ marginRight: 4 }} /> From Templates
                </button>
                <button className="btn btn-secondary" onClick={() => { setShowCustomRule(true); setShowTemplates(false); }}
                  style={{ padding: '0.35rem 0.75rem', fontSize: '0.78rem', borderRadius: 8 }}>
                  <FiPlus size={12} style={{ marginRight: 4 }} /> Create Custom Rule
                </button>
              </div>

              {showTemplates && (() => {
                const templateCategories = [...new Set(templates.map(t => t.category))].sort();
                const filteredTemplates = templates.filter(t =>
                  templateCategoryFilter === 'all' || t.category === templateCategoryFilter
                );
                return (
                <div className="card" style={{ marginBottom: '1.25rem', border: '1px solid #dbeafe' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <h3 style={{ margin: 0, fontSize: '0.95rem', color: '#1e40af' }}>Rule Templates ({templates.length})</h3>
                    <button onClick={() => { setShowTemplates(false); setTemplateCategoryFilter('all'); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
                      <FiX size={16} />
                    </button>
                  </div>
                  <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                    Click a template to configure its parameters before adding.
                  </p>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                    <button onClick={() => setTemplateCategoryFilter('all')}
                      style={{
                        padding: '0.3rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                        border: templateCategoryFilter === 'all' ? '1px solid #2563eb' : '1px solid #d1d5db',
                        background: templateCategoryFilter === 'all' ? '#dbeafe' : 'white',
                        color: templateCategoryFilter === 'all' ? '#1e40af' : '#6b7280'
                      }}>All ({templates.length})</button>
                    {templateCategories.map(cat => {
                      const count = templates.filter(t => t.category === cat).length;
                      return (
                        <button key={cat} onClick={() => setTemplateCategoryFilter(cat)}
                          style={{
                            padding: '0.3rem 0.65rem', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                            border: templateCategoryFilter === cat ? '1px solid #2563eb' : '1px solid #d1d5db',
                            background: templateCategoryFilter === cat ? '#dbeafe' : 'white',
                            color: templateCategoryFilter === cat ? '#1e40af' : '#6b7280'
                          }}>{cat} ({count})</button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
                    {filteredTemplates.map(t => {
                      const Icon = TEMPLATE_ICONS[t.icon] || FiSettings;
                      const alreadyAdded = templateAlreadyAdded(t);
                      return (
                        <div key={t.id}
                          onClick={() => !alreadyAdded && setTemplateToConfig(t)}
                          style={{
                            padding: '0.85rem', borderRadius: 10,
                            cursor: alreadyAdded ? 'not-allowed' : 'pointer',
                            border: `2px solid ${alreadyAdded ? '#d1d5db' : '#e5e7eb'}`,
                            background: alreadyAdded ? '#f9fafb' : 'white',
                            opacity: alreadyAdded ? 0.6 : 1,
                            transition: 'all 0.15s',
                            position: 'relative'
                          }}>
                          {alreadyAdded && (
                            <div style={{
                              position: 'absolute', top: 8, right: 8,
                              padding: '2px 8px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600,
                              background: '#d1fae5', color: '#065f46'
                            }}>Added</div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: 8,
                              background: alreadyAdded ? '#e5e7eb' : '#f3f4f6',
                              display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                              <Icon size={16} style={{ color: alreadyAdded ? '#9ca3af' : '#6b7280' }} />
                            </div>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#111' }}>{t.name}</div>
                              <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{t.category}</div>
                            </div>
                          </div>
                          <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0, lineHeight: 1.4 }}>{t.description}</p>
                          <div style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: '#9ca3af' }}>
                            {t.operator} {t.default_value?.length > 40 ? t.default_value.substring(0, 40) + '...' : t.default_value}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })()}

              {baselineRules.length > 0 ? (
                <div>
                  <div style={{
                    display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap'
                  }}>
                    <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                      <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                      <input value={ruleSearch} onChange={e => setRuleSearch(e.target.value)}
                        placeholder="Search rules..."
                        style={{ width: '100%', padding: '0.45rem 0.6rem 0.45rem 2rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem' }} />
                    </div>
                    <select value={ruleCategoryFilter} onChange={e => setRuleCategoryFilter(e.target.value)}
                      style={{ padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.82rem', background: 'white' }}>
                      <option value="all">All Categories</option>
                      {Object.keys(RULE_CATEGORIES_BY_PROVIDER[cloudProvider] || RULE_CATEGORIES_BY_PROVIDER.azure).map(cat => (
                        <option key={cat} value={cat}>{cat} ({categoryCounts[cat] || 0})</option>
                      ))}
                    </select>
                    <div style={{
                      padding: '0.35rem 0.75rem', borderRadius: 8, background: '#dbeafe', fontSize: '0.78rem', fontWeight: 600, color: '#2563eb'
                    }}>
                      {baselineRules.filter(r => r.is_enabled).length} enabled / {baselineRules.length} total
                    </div>
                  </div>

                  {Object.entries(groupedRules).map(([catName, rules]) => {
                    const catInfo = (RULE_CATEGORIES_BY_PROVIDER[cloudProvider] || RULE_CATEGORIES_BY_PROVIDER.azure)[catName] || { icon: '\uD83D\uDCCB', color: '#6b7280' };
                    const isCollapsed = collapsedCategories[catName] === true;
                    return (
                      <div key={catName} className="card" style={{ marginBottom: '0.75rem', padding: 0, overflow: 'hidden' }}>
                        <div onClick={() => setCollapsedCategories(prev => ({ ...prev, [catName]: !isCollapsed }))}
                          style={{
                            padding: '0.75rem 1rem', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '0.6rem',
                            background: `linear-gradient(135deg, ${catInfo.color}08, ${catInfo.color}04)`,
                            borderBottom: isCollapsed ? 'none' : '1px solid #e5e7eb'
                          }}>
                          {isCollapsed ? <FiChevronRight size={14} color="#6b7280" /> : <FiChevronDown size={14} color="#6b7280" />}
                          <span style={{ fontSize: '1rem' }}>{catInfo.icon}</span>
                          <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151' }}>{catName}</span>
                          <span style={{
                            marginLeft: 'auto', padding: '0.1rem 0.5rem', borderRadius: 10,
                            fontSize: '0.72rem', fontWeight: 600, background: `${catInfo.color}15`, color: catInfo.color
                          }}>{rules.length} rule{rules.length !== 1 ? 's' : ''}</span>
                        </div>
                        {!isCollapsed && (
                          <div style={{ padding: '0.5rem' }}>
                            {rules.map(r => {
                              const sevWeight = { critical: '8x', high: '5x', medium: '3x', low: '1x' }[r.severity] || '1x';
                              return (
                                <div key={r.id} style={{
                                  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.6rem 0.75rem',
                                  borderRadius: 8, border: '1px solid #f3f4f6',
                                  background: r.is_enabled ? 'white' : '#fafafa', opacity: r.is_enabled ? 1 : 0.65,
                                  marginBottom: '0.35rem', flexWrap: 'wrap'
                                }}>
                                  <button onClick={() => handleToggleRule(r.id)} title={r.is_enabled ? 'Disable rule' : 'Enable rule'}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: r.is_enabled ? '#2563eb' : '#9ca3af', padding: 0, flexShrink: 0 }}>
                                    {r.is_enabled ? <FiToggleRight size={22} /> : <FiToggleLeft size={22} />}
                                  </button>
                                  <div style={{ flex: 1, minWidth: 180 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111' }}>{r.name}</div>
                                    <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: 2 }}>
                                      {r.target_resource_type === '*' ? 'All resources' : r.target_resource_type.split('/').pop()}
                                      {r.target_scope !== '*' && ` in ${r.target_scope}`}
                                      {' \u2192 '}
                                      {r.property_path} {OPERATOR_LABELS[r.operator] || r.operator}{' '}
                                      {r.expected_value.length > 40 ? r.expected_value.substring(0, 40) + '...' : r.expected_value}
                                    </div>
                                  </div>
                                  <span style={{
                                    padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.68rem', fontWeight: 600,
                                    background: SEVERITY_BG[r.severity], color: SEVERITY_COLORS[r.severity], textTransform: 'capitalize'
                                  }}>{r.severity}</span>
                                  <span title="Risk weight" style={{
                                    padding: '0.15rem 0.4rem', borderRadius: 6, fontSize: '0.65rem', fontWeight: 700,
                                    background: '#f3f4f6', color: '#6b7280'
                                  }}>{sevWeight}</span>
                                  <button onClick={() => setEditingRule(r)} title="Edit rule"
                                    style={{ padding: '0.25rem', borderRadius: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                                    <FiEdit2 size={13} />
                                  </button>
                                  {ruleDeleteConfirm === r.id ? (
                                    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                      <button onClick={() => handleDeleteRule(r.id)}
                                        style={{ padding: '0.2rem 0.4rem', fontSize: '0.68rem', borderRadius: 4, background: '#dc2626', color: 'white', border: 'none', cursor: 'pointer' }}>Yes</button>
                                      <button onClick={() => setRuleDeleteConfirm(null)}
                                        style={{ padding: '0.2rem 0.4rem', fontSize: '0.68rem', borderRadius: 4, background: '#e5e7eb', color: '#374151', border: 'none', cursor: 'pointer' }}>No</button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setRuleDeleteConfirm(r.id)} title="Delete rule"
                                      style={{ padding: '0.25rem', borderRadius: 4, background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db' }}>
                                      <FiTrash2 size={13} />
                                    </button>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {Object.keys(groupedRules).length === 0 && ruleSearch && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                      <FiSearch size={24} style={{ marginBottom: '0.5rem', color: '#d1d5db' }} />
                      <p style={{ fontSize: '0.85rem' }}>No rules match "{ruleSearch}"</p>
                    </div>
                  )}

                  <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: '0.78rem', color: '#92400e' }}>
                    <FiAlertTriangle size={13} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    After adding or changing rules, click "Re-detect Drift" on the Drift Results tab to evaluate rules against current resources.
                  </div>
                </div>
              ) : (
                <div className="card" style={{
                  textAlign: 'center', padding: '3rem', color: '#9ca3af',
                  background: '#f9fafb', border: '1px dashed #d1d5db'
                }}>
                  <FiSettings size={28} style={{ marginBottom: '0.75rem', color: '#d1d5db' }} />
                  <p style={{ fontWeight: 500, color: '#6b7280', marginBottom: '0.25rem' }}>No rules defined for this baseline</p>
                  <p style={{ fontSize: '0.78rem', marginBottom: '1rem' }}>Add governance rules from templates or build custom rules to enforce policies during drift detection.</p>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                    <button className="btn btn-primary" onClick={() => setShowTemplates(true)}
                      style={{ padding: '0.45rem 1rem', borderRadius: 8, fontSize: '0.82rem' }}>
                      <FiPlus size={13} style={{ marginRight: 4 }} /> Browse Templates
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowCustomRule(true)}
                      style={{ padding: '0.45rem 1rem', borderRadius: 8, fontSize: '0.82rem' }}>
                      <FiPlus size={13} style={{ marginRight: 4 }} /> Create Custom Rule
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === 'policies' && (
        <div>
          {policiesLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <FiLoader size={28} className="spin" style={{ color: '#6366f1', marginBottom: '0.75rem' }} />
              <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Loading cloud policies...</p>
            </div>
          ) : !cloudPolicies ? (
            <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
              <FiLock size={32} style={{ color: '#d1d5db', marginBottom: '0.75rem' }} />
              <p style={{ fontWeight: 500, color: '#6b7280' }}>No cloud policy data available</p>
              <p style={{ fontSize: '0.78rem', color: '#9ca3af' }}>Run a sync to retrieve cloud policies for this tenant.</p>
            </div>
          ) : (() => {
            const summary = cloudPolicies.summary;
            const providerLabel = cloudPolicies.provider_label;
            const allPolicies = cloudPolicies.policies || [];
            const categories = cloudPolicies.categories || {};
            const recommended = cloudPolicies.recommended_policies || [];

            const filteredPolicies = allPolicies.filter(p => {
              if (policySearch && !p.name.toLowerCase().includes(policySearch.toLowerCase()) && !(p.description || '').toLowerCase().includes(policySearch.toLowerCase())) return false;
              if (policyCategoryFilter !== 'all' && p.category !== policyCategoryFilter) return false;
              if (policyEffectFilter !== 'all' && p.effect !== policyEffectFilter) return false;
              return true;
            });

            const groupedByCategory = {};
            filteredPolicies.forEach(p => {
              if (!groupedByCategory[p.category]) groupedByCategory[p.category] = [];
              groupedByCategory[p.category].push(p);
            });

            const effectColors = {
              'Deny': { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
              'Audit': { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
              'AuditIfNotExists': { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
              'DeployIfNotExists': { bg: '#f0f9ff', color: '#0369a1', border: '#bae6fd' },
              'Modify': { bg: '#faf5ff', color: '#7c3aed', border: '#ddd6fe' },
              'Deny (SCP)': { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
              'Active': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
              'Enforced': { bg: '#f0fdf4', color: '#166534', border: '#bbf7d0' },
            };

            const statusIcon = (status) => {
              if (status === 'compliant') return <FiCheck size={13} style={{ color: '#059669' }} />;
              if (status === 'non_compliant') return <FiAlertTriangle size={13} style={{ color: '#dc2626' }} />;
              return <FiInfo size={13} style={{ color: '#d97706' }} />;
            };

            const allEffects = [...new Set(allPolicies.map(p => p.effect))].sort();
            const allCategories = Object.keys(categories).sort();

            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
                    <FiLock size={16} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                    {providerLabel}
                  </h3>
                  <span style={{ fontSize: '0.72rem', color: '#6b7280', background: '#f3f4f6', padding: '0.2rem 0.5rem', borderRadius: 6 }}>
                    {summary.total_policies} active policies
                  </span>
                  {summary.baseline_rules_active > 0 && (
                    <span style={{ fontSize: '0.72rem', color: '#1e40af', background: '#dbeafe', padding: '0.2rem 0.5rem', borderRadius: 6 }}>
                      {summary.baseline_rules_active} baseline rules mapped
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <div className="card" style={{ padding: '0.85rem', textAlign: 'center', borderLeft: '3px solid #6366f1' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#6366f1' }}>{summary.total_policies}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>Total Policies</div>
                  </div>
                  <div className="card" style={{ padding: '0.85rem', textAlign: 'center', borderLeft: '3px solid #059669' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#059669' }}>{summary.enforcing}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>Enforcing</div>
                  </div>
                  <div className="card" style={{ padding: '0.85rem', textAlign: 'center', borderLeft: '3px solid #d97706' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#d97706' }}>{summary.auditing}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>Auditing</div>
                  </div>
                  <div className="card" style={{ padding: '0.85rem', textAlign: 'center', borderLeft: '3px solid #2563eb' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2563eb' }}>{summary.compliance_rate}%</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>Compliance Rate</div>
                  </div>
                  <div className="card" style={{ padding: '0.85rem', textAlign: 'center', borderLeft: '3px solid #dc2626' }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626' }}>{summary.resources_non_compliant}</div>
                    <div style={{ fontSize: '0.72rem', color: '#6b7280', fontWeight: 500 }}>Non-Compliant Resources</div>
                  </div>
                </div>

                <div className="card" style={{ marginBottom: '1.25rem', padding: '0.85rem' }}>
                  <h4 style={{ margin: '0 0 0.65rem', fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Policy Coverage by Category</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0.5rem' }}>
                    {allCategories.map(cat => {
                      const c = categories[cat];
                      const pct = Math.round((c.compliant / Math.max(c.total, 1)) * 100);
                      return (
                        <div key={cat} style={{
                          padding: '0.6rem 0.75rem', borderRadius: 8, border: '1px solid #e5e7eb',
                          background: c.non_compliant > 0 ? '#fffbeb' : '#f0fdf4'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>{cat}</span>
                            <span style={{
                              fontSize: '0.68rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 4,
                              background: pct === 100 ? '#dcfce7' : pct >= 80 ? '#fef9c3' : '#fee2e2',
                              color: pct === 100 ? '#166534' : pct >= 80 ? '#854d0e' : '#991b1b'
                            }}>{pct}%</span>
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.68rem', color: '#6b7280' }}>
                            <span>{c.total} policies</span>
                            <span style={{ color: '#059669' }}>{c.enforcing} enforcing</span>
                            <span style={{ color: '#d97706' }}>{c.auditing} auditing</span>
                          </div>
                          <div style={{ marginTop: '0.35rem', height: 4, borderRadius: 2, background: '#e5e7eb', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: pct === 100 ? '#22c55e' : pct >= 80 ? '#eab308' : '#ef4444', transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: 300 }}>
                    <FiSearch size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                    <input
                      value={policySearch} onChange={e => setPolicySearch(e.target.value)}
                      placeholder="Search policies..."
                      style={{ width: '100%', padding: '0.45rem 0.75rem 0.45rem 2rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.8rem' }}
                    />
                  </div>
                  <select value={policyCategoryFilter} onChange={e => setPolicyCategoryFilter(e.target.value)}
                    style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.8rem', background: 'white' }}>
                    <option value="all">All Categories</option>
                    {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <select value={policyEffectFilter} onChange={e => setPolicyEffectFilter(e.target.value)}
                    style={{ padding: '0.45rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.8rem', background: 'white' }}>
                    <option value="all">All Effects</option>
                    {allEffects.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Showing {filteredPolicies.length} of {allPolicies.length}
                  </span>
                </div>

                {Object.keys(groupedByCategory).sort().map(cat => (
                  <div key={cat} className="card" style={{ marginBottom: '0.75rem', padding: '0' }}>
                    <div
                      onClick={() => setExpandedPolicy(expandedPolicy === cat ? null : cat)}
                      style={{
                        padding: '0.75rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                        background: '#f9fafb', borderRadius: expandedPolicy === cat ? '12px 12px 0 0' : 12
                      }}
                    >
                      {expandedPolicy === cat ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                      <span style={{ fontWeight: 600, fontSize: '0.85rem', color: '#374151' }}>{cat}</span>
                      <span style={{ fontSize: '0.7rem', color: '#6b7280', background: '#e5e7eb', padding: '0.1rem 0.45rem', borderRadius: 8 }}>
                        {groupedByCategory[cat].length}
                      </span>
                      <div style={{ flex: 1 }} />
                      {(() => {
                        const nc = groupedByCategory[cat].filter(p => p.compliance_status !== 'compliant').length;
                        return nc > 0 ? (
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626', background: '#fee2e2', padding: '0.15rem 0.5rem', borderRadius: 6 }}>
                            {nc} non-compliant
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669', background: '#dcfce7', padding: '0.15rem 0.5rem', borderRadius: 6 }}>
                            All compliant
                          </span>
                        );
                      })()}
                    </div>
                    {expandedPolicy === cat && (
                      <div style={{ padding: '0.5rem' }}>
                        {groupedByCategory[cat].map(p => {
                          const ec = effectColors[p.effect] || { bg: '#f3f4f6', color: '#374151', border: '#d1d5db' };
                          const compPct = Math.round((p.compliant_resources / Math.max(p.total_resources, 1)) * 100);
                          const isPolicyExpanded = expandedPolicyRow === p.id;
                          const policyAffected = p.affected_resources || [];
                          return (
                            <div key={p.id} style={{
                              margin: '0.35rem 0', borderRadius: 8,
                              border: `1px solid ${p.compliance_status === 'compliant' ? '#d1fae5' : '#fecaca'}`,
                              background: p.compliance_status === 'compliant' ? '#fafffe' : '#fffbfb',
                              overflow: 'hidden',
                            }}>
                              <div style={{ padding: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', paddingTop: '0.1rem' }}>
                                  {p.non_compliant_resources > 0 && (
                                    <button
                                      onClick={() => setExpandedPolicyRow(isPolicyExpanded ? null : p.id)}
                                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                    >
                                      {isPolicyExpanded ? <FiChevronDown size={14} color="#6b7280" /> : <FiChevronRight size={14} color="#6b7280" />}
                                    </button>
                                  )}
                                  {statusIcon(p.compliance_status)}
                                </div>
                                <div style={{ flex: 1, minWidth: 200 }}>
                                  <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111827', marginBottom: '0.2rem', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{p.name}</div>
                                  <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '0.4rem', wordBreak: 'break-word', overflowWrap: 'anywhere', lineHeight: 1.5 }}>{p.description}</div>
                                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{
                                      fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.5rem', borderRadius: 4,
                                      background: ec.bg, color: ec.color, border: `1px solid ${ec.border}`
                                    }}>{p.effect}</span>
                                    <span style={{
                                      fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 4,
                                      background: p.compliance_status === 'compliant' ? '#dcfce7' : p.compliance_status === 'non_compliant' ? '#fee2e2' : '#fef9c3',
                                      color: p.compliance_status === 'compliant' ? '#166534' : p.compliance_status === 'non_compliant' ? '#991b1b' : '#854d0e'
                                    }}>{p.compliance_status === 'compliant' ? 'Compliant' : p.compliance_status === 'non_compliant' ? 'Non-Compliant' : 'Flagged'}</span>
                                    {p.severity && (
                                      <span style={{
                                        fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: 4,
                                        background: SEVERITY_BG[p.severity] || '#f3f4f6', color: SEVERITY_COLORS[p.severity] || '#374151'
                                      }}>{p.severity}</span>
                                    )}
                                    <button
                                      onClick={() => handleAskAI('cloud policy', p.name, p.description, cat, p.compliance_status, p.severity || '', policyAffected)}
                                      disabled={aiLoading === `cloud policy::${p.name}`}
                                      style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 3,
                                        padding: '0.15rem 0.45rem', borderRadius: 5, border: '1px solid #c7d2fe',
                                        background: aiLoading === `cloud policy::${p.name}` ? '#e0e7ff' : '#eef2ff',
                                        color: '#4f46e5', fontSize: '0.65rem', fontWeight: 600, cursor: 'pointer',
                                        opacity: aiLoading === `cloud policy::${p.name}` ? 0.7 : 1,
                                        marginLeft: '0.25rem',
                                      }}
                                      title="Get AI remediation guidance"
                                    >
                                      {aiLoading === `cloud policy::${p.name}` ? <FiLoader size={9} className="spin" /> : <span style={{ fontSize: '0.65rem' }}>&#10024;</span>}
                                      Ask AI
                                    </button>
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right', minWidth: 120 }}>
                                  <div style={{ fontSize: '0.73rem', color: '#374151', fontWeight: 600 }}>
                                    {p.compliant_resources}/{p.total_resources} compliant
                                  </div>
                                  <div style={{ marginTop: '0.25rem', height: 5, borderRadius: 3, background: '#e5e7eb', overflow: 'hidden', width: 100 }}>
                                    <div style={{ height: '100%', width: `${compPct}%`, borderRadius: 3, background: compPct === 100 ? '#22c55e' : compPct >= 80 ? '#eab308' : '#ef4444' }} />
                                  </div>
                                  <div style={{ fontSize: '0.65rem', color: '#9ca3af', marginTop: '0.2rem' }}>
                                    Eval: {new Date(p.last_evaluated).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>

                              {isPolicyExpanded && policyAffected.length > 0 && (
                                <div style={{ padding: '0 0.75rem 0.75rem 2.25rem', borderTop: '1px solid #f3f4f6' }}>
                                  <div style={{ fontSize: '0.73rem', fontWeight: 700, color: '#374151', margin: '0.6rem 0 0.4rem', display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <FiAlertTriangle size={11} color="#dc2626" />
                                    {policyAffected.length} Non-Compliant Resource{policyAffected.length !== 1 ? 's' : ''}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                    {policyAffected.map((res, ri) => (
                                      <div key={ri} style={{
                                        display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.6rem',
                                        background: '#fef2f2', borderRadius: 6, border: '1px solid #fecaca',
                                      }}>
                                        <FiAlertTriangle size={10} color="#dc2626" />
                                        <span style={{ fontSize: '0.73rem', fontWeight: 600, color: '#374151', flex: 1, wordBreak: 'break-all' }}>
                                          {res.resource_name || 'Unknown'}
                                        </span>
                                        {res.resource_type && (
                                          <span style={{ fontSize: '0.63rem', color: '#6b7280', background: '#f3f4f6', padding: '0.1rem 0.35rem', borderRadius: 4, whiteSpace: 'nowrap' }}>
                                            {res.resource_type.split('/').pop()}
                                          </span>
                                        )}
                                        {res.resource_group && (
                                          <span style={{ fontSize: '0.63rem', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                            RG: {res.resource_group}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {isPolicyExpanded && policyAffected.length === 0 && p.non_compliant_resources > 0 && (
                                <div style={{ padding: '0.5rem 0.75rem 0.75rem 2.25rem', borderTop: '1px solid #f3f4f6' }}>
                                  <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                    {p.non_compliant_resources} non-compliant resource{p.non_compliant_resources !== 1 ? 's' : ''} detected. Detailed resource information may not be available for all policies.
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}

                {recommended.length > 0 && (
                  <div className="card" style={{ marginTop: '1.25rem', border: '1px solid #dbeafe' }}>
                    <h4 style={{ margin: '0 0 0.15rem', fontSize: '0.9rem', fontWeight: 700, color: '#1e40af' }}>
                      <FiZap size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                      Recommended Policies
                    </h4>
                    <p style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '0.85rem' }}>
                      These policies are recommended for your environment but are not currently active. Enabling them would strengthen your governance posture.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.65rem' }}>
                      {recommended.map((r, i) => (
                        <div key={i} style={{
                          padding: '0.75rem', borderRadius: 8, border: '1px dashed #93c5fd', background: '#f8faff'
                        }}>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#1e40af', marginBottom: '0.2rem' }}>{r.name}</div>
                          <div style={{ fontSize: '0.73rem', color: '#6b7280', marginBottom: '0.35rem' }}>{r.reason}</div>
                          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.65rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 4, background: '#dbeafe', color: '#1e40af' }}>{r.category}</span>
                            <span style={{
                              fontSize: '0.65rem', fontWeight: 600, padding: '0.1rem 0.4rem', borderRadius: 4,
                              background: r.priority === 'critical' ? '#fee2e2' : r.priority === 'high' ? '#fff7ed' : '#fef9c3',
                              color: r.priority === 'critical' ? '#991b1b' : r.priority === 'high' ? '#9a3412' : '#854d0e'
                            }}>{r.priority} priority</span>
                            {r.baseline_rule_map && (
                              <span style={{ fontSize: '0.65rem', fontWeight: 500, padding: '0.1rem 0.4rem', borderRadius: 4, background: '#f3e8ff', color: '#6b21a8' }}>
                                Maps to: {r.baseline_rule_map}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      )}

      {templateToConfig && (
        <TemplateConfigModal
          template={templateToConfig}
          existingRules={baselineRules}
          onClose={() => setTemplateToConfig(null)}
          onAdd={handleAddRuleFromTemplate}
        />
      )}

      {showCustomRule && (
        <CustomRuleBuilder
          existingRules={baselineRules}
          onClose={() => setShowCustomRule(false)}
          onAdd={handleAddCustomRule}
          cloudProvider={cloudProvider}
        />
      )}

      {editingRule && (
        <EditRuleModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={handleUpdateRule}
        />
      )}

      {aiGuide && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '1rem',
        }} onClick={() => setAiGuide(null)}>
          <div style={{
            background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720,
            maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{
              position: 'sticky', top: 0, background: '#fff', padding: '1.25rem 1.5rem 0.75rem',
              borderBottom: '1px solid #e5e7eb', zIndex: 1, borderRadius: '12px 12px 0 0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '1rem' }}>&#10024;</span>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1f2937' }}>AI Remediation Guide</h3>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#6b7280' }}>{aiGuide.resourceName}</p>
                </div>
                <button onClick={() => setAiGuide(null)} style={{
                  background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer',
                  color: '#9ca3af', padding: '0.25rem', lineHeight: 1,
                }}>&times;</button>
              </div>
            </div>

            <div style={{ padding: '1.25rem 1.5rem' }}>
              <div style={{
                background: '#f0f4ff', borderRadius: 8, padding: '0.85rem 1rem',
                marginBottom: '1rem', border: '1px solid #dbeafe',
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Summary</div>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>{aiGuide.summary}</p>
              </div>

              <div style={{
                background: '#f0fdf4', borderRadius: 8, padding: '0.85rem 1rem',
                marginBottom: '1.25rem', border: '1px solid #bbf7d0',
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#059669', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Score Impact</div>
                <p style={{ margin: 0, fontSize: '0.82rem', color: '#166534', lineHeight: 1.5 }}>{aiGuide.scoreImpact}</p>
              </div>

              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#1f2937' }}>Step-by-Step Remediation</h4>

              {(aiGuide.steps || []).map((step, idx) => (
                <div key={idx} style={{
                  marginBottom: '1rem', borderRadius: 8, border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.6rem',
                    padding: '0.7rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb',
                  }}>
                    <span style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem',
                      fontWeight: 700, color: '#fff', background: '#6366f1', flexShrink: 0,
                    }}>{step.stepNumber}</span>
                    <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1f2937' }}>{step.title}</span>
                    {step.estimatedTime && (
                      <span style={{
                        marginLeft: 'auto', fontSize: '0.68rem', color: '#9ca3af',
                        background: '#f3f4f6', padding: '2px 8px', borderRadius: 10,
                      }}>{step.estimatedTime}</span>
                    )}
                  </div>
                  <div style={{ padding: '0.75rem 1rem' }}>
                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.8rem', color: '#374151', lineHeight: 1.5 }}>{step.description}</p>

                    {step.portalSteps && step.portalSteps.length > 0 && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Portal Steps</div>
                        <ol style={{ margin: 0, paddingLeft: '1.2rem' }}>
                          {step.portalSteps.map((ps, pi) => (
                            <li key={pi} style={{ fontSize: '0.78rem', color: '#4b5563', marginBottom: '0.2rem', lineHeight: 1.4 }}>{ps}</li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {step.commands && step.commands.length > 0 && (
                      <div style={{ marginBottom: '0.5rem' }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.3rem' }}>CLI Commands</div>
                        {step.commands.map((cmd, ci) => (
                          <pre key={ci} style={{
                            background: '#1f2937', color: '#e5e7eb', padding: '0.5rem 0.75rem',
                            borderRadius: 6, fontSize: '0.72rem', overflowX: 'auto',
                            margin: '0.2rem 0', lineHeight: 1.5,
                          }}>{cmd}</pre>
                        ))}
                      </div>
                    )}

                    {step.verificationStep && (
                      <div style={{
                        background: '#f0fdf4', borderRadius: 6, padding: '0.5rem 0.75rem',
                        border: '1px solid #bbf7d0',
                      }}>
                        <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669', marginBottom: '0.15rem' }}>Verification</div>
                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#166534', lineHeight: 1.4 }}>{step.verificationStep}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {aiGuide.additionalNotes && aiGuide.additionalNotes.length > 0 && (
                <div style={{ marginTop: '0.75rem', background: '#fffbeb', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #fde68a' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#d97706', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Important Notes</div>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {aiGuide.additionalNotes.map((note, ni) => (
                      <li key={ni} style={{ fontSize: '0.78rem', color: '#92400e', marginBottom: '0.2rem', lineHeight: 1.4 }}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {aiGuide.automationTip && (
                <div style={{ marginTop: '0.75rem', background: '#f0f4ff', borderRadius: 8, padding: '0.75rem 1rem', border: '1px solid #dbeafe' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Automation Tip</div>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#1e40af', lineHeight: 1.4 }}>{aiGuide.automationTip}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
}

export default function DriftMonitoring() {
  return <NoTenantGuard><DriftMonitoringContent /></NoTenantGuard>;
}
